import { canCreateSupplierDrafts } from '../modules/orders/supplierDraftGrouping/canCreateSupplierDrafts';
import OrderDocumentCompletenessSection from '../modules/orders/sections/OrderDocumentCompletenessSection';
import SupplierDraftGrouping from '../modules/orders/supplierDraftGrouping/SupplierDraftGrouping';
import OrderWarehouseSection from '../modules/orders/sections/OrderWarehouseSection';
import { createOrderTransportActions } from '../modules/orders/orderTransportActions';
import { pendingInvoiceCostLabel } from '../modules/reports/pendingInvoiceCost';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Table, Tag, Button, Modal, Form, Select, Input, InputNumber, Switch, Upload, App, Timeline, Space, Spin, Tooltip, Tabs, Alert, Dropdown, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, FileTextOutlined, TruckOutlined, DollarOutlined, FilePdfOutlined, CheckSquareOutlined, UserOutlined, CalendarOutlined, MailOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import api from '../services/api';
import { formatCurrencyLocalized } from '../utils/currency';
import { formatLocalizedPercent, formatLocalizedQuantity } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { formatDateEuropean, formatDateTimeEuropean } from '../utils/dateUtils';
import { buildSafePdfFileName } from '../utils/pdfFileName';
import type { OrderSettlementDto } from '../dto/orderSettlement';
import { Cost, OrderMarginHistoryEntry, OrderSettlementEntry, getDisplayOrderNumber, getDisplayOrderNumberAfterUpdate } from '../modules/orders/types';
import type { OrderMarginHistoryLineImage, OrderProduct } from '../types/order';
import { getOrderProductDisplayName, withLineDimensions } from '../modules/orders/orderProductDisplay';
import { getApiErrorMessage } from '../utils/apiError';
import { getOrderErrorMessage, getOrderStatusConflictMessage, isOrderStatusConflictError, isStickyOrderStatusRefusal, ORDER_ERROR_CODE_KEYS } from '../modules/orders/orderErrorMessages';
import { getOrderNumberTaken, getOrderProductSaveErrorMessage, type OrderNumberTaken } from '../modules/orders/orderProductSaveErrors';
import OrderNumberTakenHelp from '../modules/orders/components/OrderNumberTakenHelp';
import { getMarginHistoryReasonLabel } from '../modules/orders/marginHistoryReasons';
import { findBlockedAction, getBlockedActionMessage } from '../modules/warehouse/blockedActions';
import { useScopedFeedback } from '../utils/feedback';
import { usePageHeaderTitleOverride } from '../modules/navigation/pageHeaderTitleOverride';
import OrderNotificationAssignments from '../components/OrderNotificationAssignments';
import CommunicationDetail from '../modules/notifications/components/CommunicationDetail';
import type { CommunicationLogDto } from '../modules/notifications/api/communicationsApi';
import PresenceBanner from '../components/presence/PresenceBanner';
import { usePresence } from '../hooks/usePresence';
import { internalTaskApi, InternalTask } from '../services/internalTaskApi';
import { canUseInternalTasks, canViewOrderCommunications } from '../modules/salesRep/salesRepAccessPolicy';
import { getAuthFileUrl } from '../utils/fileUrl';
import {
  archiveOrder,
  convertQuoteToOrder,
  downloadOrderPdf,
  fetchOrderMarginHistory,
  OrderDocumentLanguage,
  unarchiveOrder,
  updateOrder,
} from '../modules/orders/api/ordersApi';
import { fetchOrderSettlement } from '../modules/orders/api/orderSettlementApi';
import {
  refreshOrderFinanceReadModels,
  runOrderFinanceMutation,
} from '../modules/orders/orderFinanceMutation';
import {
  MAX_COST_AMOUNT,
  deleteOrderCost,
  isManagedTransportCost,
  isManualCustomerOrderCost,
  isOperationalCustomerOrderCost,
  saveOrderCost,
} from '../modules/orders/api/orderCostsApi';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import { resolveOrderDeliveryAddress } from '../utils/orderDeliveryAddress';
import { canManageTransportRoutes, canReadTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { withTransportReturnContext } from '../modules/transports/transportReturnContext';
import type { OrderTransportsDto } from '../dto/transport';
import {
  canChangeCustomerOrderStatus,
  canDeleteCustomerOrderTransportLink,
  canEditCustomerOrderDate,
  canEditCustomerOrderCommercialFields,
  canEditCustomerOrderAccountManager,
  canEditCustomerOrderForecastMonth,
  canReadCustomerOrderDeliverySchedule,
  canEditCustomerOrderDeliveryMode,
  canEditCustomerOrderDetails,
  canEditCustomerOrderDocumentFields,
  canEditCustomerOrderProducts,
  canEditCustomerOrderOnAnyStep,
  canManageCustomerOrderOperations,
  canDownloadCustomerOrderPdf,
  canSendCustomerEmail,
  canManageOrderNotificationAssignments,
  canDeleteCustomerOrders,
} from '../modules/orders/customerOrderAccessPolicy';
import {
  canShowOrderFinancialCompletenessStatus,
  canShowOrderSalePricesIncompleteHint,
} from '../modules/orders/orderFinancialCompletenessVisibility';
import {
  sendCustomerConfirmationEmail,
  previewCustomerConfirmationEmail,
  previewCustomerConfirmationEmailDraft,
  CustomerConfirmationPreview,
  sendCustomerStatusEmail,
  previewCustomerStatusEmail,
  previewCustomerStatusEmailDraft,
  CustomerStatusEmailPreview
} from '../modules/orders/api/orderStatusApi';
import {
  buildOrderDetailUpdateData,
  isForecastMonthUpdateNoop,
  getOrderDetailFieldNames,
  getOrderDetailInitialValues,
  OrderDetailEditableField,
} from '../modules/orders/orderDetailEditUtils';
import { customerOrderNumberRule } from '../dto/order';
import {
  OrderStatusFormValues,
  useOrderStatusChangeWithConfirmation,
} from '../modules/orders/useOrderStatusChangeWithConfirmation';
import { useOrderDetail } from '../modules/orders/queries/useOrderDetail';
import OrderConfirmationNoticeSection, {
  type OrderConfirmationNoticeSelection,
} from '../modules/orders/sections/OrderConfirmationNoticeSection';
import { startsCustomerOrderConfirmationCycle } from '../modules/orders/customerOrderStatuses';
import type { OrderSupplierDraft } from '../modules/orders/api/orderStockStatusApi';
import { buildSupplierDraftHistoryEntries } from '../modules/orders/orderSupplierDraftHistory';
import { OrderProductsSection } from '../modules/orders/sections/OrderProductsSection';
import OrderPlannedMarginSummary from '../modules/orders/sections/OrderPlannedMarginSummary';
import { promptOrderPdfConversionUnit } from '../modules/orders/orderPdfConversionPrompt';
import { OrderInfoAccordion } from '../modules/orders/sections/OrderInfoAccordion';
import { OrderDeliveryRevertModal } from '../modules/orders/sections/OrderDeliveryRevertModal';
import { ORDER_DELIVERY_REVERT_ERROR_KEYS } from '../modules/orders/api/orderDeliveryRevertApi';
import { isAdminUser } from '../services/authPermissions';
import CurrencyAmount from '../components/CurrencyAmount';
import OrderTransportsSection from '../modules/orders/sections/OrderTransportsSection';
import CustomerEmailEditorModal from '../modules/orders/sections/CustomerEmailEditorModal';
import { useSupplierOptions } from '../modules/reference-data/queries/useSupplierOptions';
import {
  PAGE_SHELL_STYLE,
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../modules/orders/orderDetailLayout';
import {
  CUSTOMER_ORDER_ACTIVE_STATUS_VALUES,
  getOrderAllowedTransitions,
} from '../modules/orders/orderStatusTransitions';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import OrderManualCloseButton from '../modules/orders/components/OrderManualCloseButton';
import OrderTransportStatusStrip from '../modules/orders/sections/OrderTransportStatusStrip';
import OrderInvoiceCoverageSection, { hasOrderInvoices, OrderInvoicesStepSection } from '../modules/orders/sections/OrderInvoiceCoverageSection';
import { fetchOrderSalesInvoices } from '../modules/invoices/api/salesInvoiceImportApi';
import type { AdvanceInvoiceBalance } from '../modules/invoices/api/invoiceWorkflowApi';
import type { OrderSalesInvoice } from '../types/orderSalesInvoice';
import { InvoiceCoverageTag } from '../modules/invoices/components/InvoiceCoverageSummary';
import { formatRemainderLines } from '../modules/invoices/invoiceCoverageLabels';
import { hasRevenueForMarginPercent } from '../modules/orders/orderFinanceDisplay';
import { getOrderMarginSummary } from '../modules/orders/orderMarginSummary';
import { useBackNavigation } from '../hooks/useBackNavigation';
import './OrderDetail.css';
import '../modules/orders/process/OrderProcess.css';
import OrderBusinessDate from '../modules/orders/sections/OrderBusinessDate';
import OrderProcessHeader from '../modules/orders/process/OrderProcessHeader';
import OrderProcessRail from '../modules/orders/process/OrderProcessRail';
import OrderStepActionBar from '../modules/orders/process/OrderStepActionBar';
import { OrderConfirmDialog, OrderDeliverDialog } from '../modules/orders/process/OrderStepDialogs';
import {
  ORDER_PROCESS_STEPS,
  canOpenOrderProcessStep,
  getOrderProcessPillKey,
  getOrderProcessPosition,
  getOrderProcessStepIndex,
  getStatusAfterWzIssued,
  isOrderProcessStepReadOnly,
  type OrderProcessStepKey,
} from '../modules/orders/process/orderProcessSteps';
import {
  ORDER_MARGIN_DETAILS_LABEL_KEYS,
  getOrderMarginFigure,
  getOrderMarginIncompleteLines,
  getOrderMarginVisibility,
} from '../modules/orders/process/orderMarginStage';
import { getOrderStepBar, type OrderStepPrimaryAction } from '../modules/orders/process/orderStepBar';
import { getOrderProcessFacts, getOrderStepBarOrderFacts } from '../modules/orders/process/orderProcessFacts';
import type { OrderStepContext } from '../modules/orders/process/orderStepContext';
import { emptyOfferAcceptance, type OfferAcceptance } from '../modules/orders/process/orderOfferAcceptance';
import { shortageNoticeFor, summarizeOrderNotifications } from '../modules/orders/process/orderNotificationHistory';
import { buildOfferEmailText } from '../modules/orders/process/orderOfferEmail';
import DraftStep from '../modules/orders/process/steps/DraftStep';
import OfferStep from '../modules/orders/process/steps/OfferStep';
import OrderGuideScreen from '../modules/orders/process/guide/OrderGuideScreen';
import OrderPricesDialog from '../modules/orders/process/guide/OrderPricesDialog';
import PrepaymentPaidDialog from '../modules/orders/process/guide/PrepaymentPaidDialog';
import type { OrderGuidePrepaymentFacts } from '../modules/orders/process/guide/orderGuideRules';
import { DEFAULT_PREPAYMENT_PERCENT, formatPrepaymentPercent } from '../modules/orders/orderPaymentMode';
import { isOrderGuideStep } from '../modules/orders/process/guide/orderGuideRules';
import FulfillmentStep from '../modules/orders/process/steps/FulfillmentStep';
import ReadyStep from '../modules/orders/process/steps/ReadyStep';
import DeliveryStep from '../modules/orders/process/steps/DeliveryStep';
import DeliveredStep from '../modules/orders/process/steps/DeliveredStep';
import InvoiceStep from '../modules/orders/process/steps/InvoiceStep';
import { useOrderProcessData } from '../modules/orders/queries/useOrderProcessData';
import { useOrderDraftEditor } from '../modules/orders/process/draft/useOrderDraftEditor';
import { getOrderDraftReadiness, ORDER_DRAFT_HANDOVER_WARNING_KEY, ORDER_DRAFT_SAVE_ERROR_KEY } from '../modules/orders/process/draft/orderDraftModel';
import { useOrderFifoPreview } from '../modules/orders/queries/useOrderFifoPreview';
import WzIssueModal, { type WzIssueResult } from '../modules/warehouse/components/WzIssueModal';
import OrderTransportLinkModal from '../modules/orders/sections/OrderTransportLinkModal';
import { canManageWZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import type { OrderDeliveryMode } from '../modules/transports/transportDirection';
import { canUseEditingPresence } from '../modules/presence/presenceAccessPolicy';


const isPurchaseInvoiceCost = (cost: Pick<Cost, 'category'>) => cost.category === 'PRODUCT_COST';


const getCustomerConfirmationIssueMessage = (
  reason: string | null | undefined,
  t: (key: string) => string,
  useFallback = true,
) => {
  switch (reason) {
    case 'NO_CUSTOMER_EMAIL':
      return t('orders:orderDetail.customerHasNoEmail');
    case 'ORDER_NOT_CONFIRMED':
      return t('orders:orderDetail.customerConfirmationRequiresConfirmed');
    case 'EMAIL_NOT_CONFIGURED':
      return t('orders:orderDetail.customerConfirmationEmailNotConfigured');
    case 'EMAIL_SEND_FAILED':
      return t('orders:orderDetail.customerConfirmationError');
    default:
      return reason && useFallback ? t('orders:orderDetail.customerConfirmationBlockedFallback') : '';
  }
};

const getCustomerStatusEmailIssueMessage = (
  reason: string | null | undefined,
  t: (key: string) => string,
  useFallback = true,
) => {
  switch (reason) {
    case 'NO_CUSTOMER_EMAIL':
      return t('orders:orderDetail.customerHasNoEmail');
    case 'STATUS_EMAILS_DISABLED':
      return t('orders:orderDetail.customerStatusEmailDisabled');
    case 'EMAIL_NOT_CONFIGURED':
      return t('orders:orderDetail.customerConfirmationEmailNotConfigured');
    case 'EMAIL_SEND_FAILED':
      return t('orders:orderDetail.customerStatusEmailError');
    default:
      return reason && useFallback ? t('orders:orderDetail.customerConfirmationBlockedFallback') : '';
  }
};



/** Przycisk „Szczegóły zamówienia” w nagłówku karty ZK; panel zostaje dostępny pod `?szczegoly=1`. */
const ORDER_DETAILS_BUTTON_VISIBLE = false;

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t, i18n } = useTranslation(['orders', 'common', 'tasks', 'wz', 'notifications']);
  const formatOrderMoney = (
    value: number | string | null | undefined,
    currency = 'PLN',
  ) => formatCurrencyLocalized(value, currency, i18n.language);
  const currentUser = authService.getCurrentUser();
  const presenceOthers = usePresence(
    'ORDER',
    canUseEditingPresence(currentUser) ? id || null : null,
  );
  const { modal } = App.useApp();
  // Toasts of the ZK card belong to its current action: a step change, a new
  // action or leaving the card closes them — never a sticky one (utils/feedback.ts).
  const feedback = useScopedFeedback('zk-card');
  const clearCardToasts = useCallback(() => {
    feedback.clear();
    feedback.message.destroy(ORDER_DRAFT_SAVE_ERROR_KEY);
    feedback.message.destroy(ORDER_DRAFT_HANDOVER_WARNING_KEY);
  }, [feedback]);
  useEffect(() => () => { clearCardToasts(); }, [clearCardToasts]);
  // Operator-facing identity of a cost line for toasts: the category label,
  // narrowed by the operator's own description when there is one. A Cost has no
  // `name` column, and "Koszt dodany pomyślnie" told nobody which one.
  const describeCost = (category?: string | null, description?: string | null): string => {
    const label = category ? t(`orders:costCategories.${category}`) : '';
    const detail = (description || '').trim();
    if (label && detail) return `${label} — ${detail}`;
    return detail || label || t('orders:costCategories.OTHER');
  };
  const { order, setOrder, loading, error: orderError, refresh: refreshOrder } = useOrderDetail(id);
  // Operator-facing identity of this order, used by every toast so a
  // confirmation never reads as a bare "Zamówienie zaktualizowane pomyślnie".
  const orderDisplayNumber = order ? getDisplayOrderNumber(order) : '';

  usePageHeaderTitleOverride(
    order
      ? t('orders:pageHeader.customer', { number: getDisplayOrderNumber(order) })
      : null,
  );
  const [orderTransports, setOrderTransports] = useState<OrderTransportsDto>({
    transports: [],
    productRealization: [],
  });
  const [transportLoading, setTransportLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(searchParams.get('tab') === 'transport' ? '2' : '1');
  const [costModalVisible, setCostModalVisible] = useState(false);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [deliveryRevertModalVisible, setDeliveryRevertModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const selectedStatus = Form.useWatch('status', statusForm);
  // Decision 2026-10-04: recipient choice of the confirmation dialog and the
  // confirmation draft ZD shown in the ZK history (no prices).
  const [confirmationNotice, setConfirmationNotice] = useState<OrderConfirmationNoticeSelection | null>(null);
  // ZK card by the „ZK w pełnym FIFO” mockup (decision 2026-10-04): reads
  // behind the step screens; the confirmation draft ZD comes from the same
  // stock-status read (shown in the ZK history).
  const canReadWarehouse = authService.canViewWarehouseDocuments();
  const processData = useOrderProcessData(id, {
    canReadWarehouse,
    canReadDeliverySchedule: canReadCustomerOrderDeliverySchedule(currentUser),
    refreshKey: order,
  });
  const supplierDraft: OrderSupplierDraft | null = processData.stockStatus?.supplierDraft ?? null;
  const refreshSupplierDraft = async () => { processData.refresh(); };
  // „Stwórz ofertę” on a new order lands here on the Oferta step (pages/OrderNew.tsx).
  // „Szczegóły zamówienia” (zakładki Informacje/Aktywność/Transport/Finanse/Zadania) są wyłączone z interfejsu
  // (decyzja właściciela 2026-10-05), ale gotowe w pogotowiu: panel otwiera się pod adresem `?szczegoly=1`.
  // Powrót przycisku do nagłówka: ustaw ORDER_DETAILS_BUTTON_VISIBLE na true.
  const [detailsOpen, setDetailsOpen] = useState(() => searchParams.get('szczegoly') === '1');
  // „Edytuj pozycje”: the line editor on any step after the Szkic, without going back to it.
  const [linesEditorOpen, setLinesEditorOpen] = useState(false);
  // „Koszty”: the customer order costs (manual costs added/edited here) on any step.
  const [costsDrawerOpen, setCostsDrawerOpen] = useState(false);
  const [viewedStepKey, setViewedStepKey] = useState<OrderProcessStepKey | null>(
    () => ((location.state as { zkStep?: OrderProcessStepKey } | null)?.zkStep ?? null),
  );
  const [offerAcceptance, setOfferAcceptance] = useState<OfferAcceptance>(emptyOfferAcceptance);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pricesDialogOpen, setPricesDialogOpen] = useState(false);
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [wzIssueOpen, setWzIssueOpen] = useState(false);
  const [transportLinkOpen, setTransportLinkOpen] = useState(false);
  const [savingDeliveryMode, setSavingDeliveryMode] = useState(false);
  const [prepaymentDialogOpen, setPrepaymentDialogOpen] = useState(false);
  // „Klient potwierdził” of a prepaid ZK asks first whether the payment arrived (Emil, 2026-11-02).
  const [prepaymentGateOpen, setPrepaymentGateOpen] = useState(false);
  const [savingPrepayment, setSavingPrepayment] = useState(false);
  const processFifo = useOrderFifoPreview(
    id || '',
    order?.productsLoaded ? order.products : [],
    Boolean(order?.productsLoaded) && (authService.canViewMargin() || authService.canViewCustomerOrderPlannedMargin()),
    order,
    true,
  );
  // Szkic step: the draft is edited on the card (owner 2026-10-05; draft/useOrderDraftEditor.ts).
  const draftConfirmation = useMemo(() => ({ modal, t }), [modal, t]);
  const canEditOrderDate = canEditCustomerOrderDate(currentUser, order);
  // Who may change which Szkic field — one object for the editor commands and the screen.
  const draftRights = useMemo(() => ({
    orderDate: canEditOrderDate,
    commercial: canEditCustomerOrderCommercialFields(currentUser),
    accountManager: canEditCustomerOrderAccountManager(currentUser),
    customerPayments: authService.canViewCustomerPayments() && canEditCustomerOrderDocumentFields(currentUser),
    deliveryMode: canEditCustomerOrderDeliveryMode(currentUser),
  }), [currentUser, canEditOrderDate]);
  // The save-error toast goes with the next edit of the Szkic (a field corrected, a product added).
  const saveToastsRef = useRef(feedback.message);
  saveToastsRef.current = feedback.message;
  const draftEditor = useOrderDraftEditor({
    onEdit: () => saveToastsRef.current.destroy(ORDER_DRAFT_SAVE_ERROR_KEY),
    order: order ?? null,
    // The customer price list only matters while lines are added: the current, editable Szkic.
    loadCustomerPrices: order?.status === 'NEW_INQUIRY' && canEditCustomerOrderProducts(currentUser)
      && (viewedStepKey === null || viewedStepKey === 'DRAFT'),
    caps: {
      canViewSalePrices: authService.canViewCustomerSalePrices(),
      canEditSalePrices: authService.canEditCustomerSalePrices(),
      ...draftRights,
    },
    confirmation: draftConfirmation,
  });
  // The landing step from a new order is used once; a reload starts at the current step.
  useEffect(() => {
    if ((location.state as { zkStep?: string } | null)?.zkStep) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!draftEditor.dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [draftEditor.dirty]);
  const [editingField, setEditingField] = useState<OrderDetailEditableField | null>(null);
  const [editForm] = Form.useForm();
  const [savingField, setSavingField] = useState<OrderDetailEditableField | null>(null);
  const [numberForm] = Form.useForm();
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const [numberTaken, setNumberTaken] = useState<OrderNumberTaken | null>(null);
  const numberFormUseManual = Form.useWatch('useManualNumbering', numberForm);
  const [orderDocumentLanguage, setOrderDocumentLanguage] = useState<OrderDocumentLanguage>(
    i18n.language.startsWith('pl') ? 'pl' : 'en'
  );
  const [orderDocumentDownloading, setOrderDocumentDownloading] = useState(false);
  const [customerEmailModalVisible, setCustomerEmailModalVisible] = useState(false);
  const [customerEmailSending, setCustomerEmailSending] = useState(false);
  const [customerEmailPreview, setCustomerEmailPreview] = useState<CustomerConfirmationPreview | null>(null);
  const [customerEmailDefaultPreview, setCustomerEmailDefaultPreview] = useState<CustomerConfirmationPreview | null>(null);
  const [customerEmailPreviewLoading, setCustomerEmailPreviewLoading] = useState(false);
  const [customerEmailSubject, setCustomerEmailSubject] = useState('');
  const [customerEmailMessage, setCustomerEmailMessage] = useState('');
  const [customerEmailEditing, setCustomerEmailEditing] = useState(false);
  const [statusEmailModalVisible, setStatusEmailModalVisible] = useState(false);
  const [statusEmailSending, setStatusEmailSending] = useState(false);
  const [statusEmailPreview, setStatusEmailPreview] = useState<CustomerStatusEmailPreview | null>(null);
  const [statusEmailDefaultPreview, setStatusEmailDefaultPreview] = useState<CustomerStatusEmailPreview | null>(null);
  const [statusEmailPreviewLoading, setStatusEmailPreviewLoading] = useState(false);
  const [statusEmailSubject, setStatusEmailSubject] = useState('');
  const [statusEmailMessage, setStatusEmailMessage] = useState('');
  const [statusEmailEditing, setStatusEmailEditing] = useState(false);
  // „Wyślij do klienta” on the Oferta step reuses the customer status e-mail
  // with the offer as its edited text (decision: existing mechanism, no PDF attachment).
  const [statusEmailPurpose, setStatusEmailPurpose] = useState<'status' | 'offer'>('status');
  // One notification log on the card: the „Aktywność” timeline; the body opens here.
  const [openedNotificationLog, setOpenedNotificationLog] = useState<CommunicationLogDto | null>(null);
  const [marginHistory, setMarginHistory] = useState<OrderMarginHistoryEntry[]>([]);
  const [orderTasks, setOrderTasks] = useState<InternalTask[]>([]);
  const [orderTasksLoading, setOrderTasksLoading] = useState(false);
  const [submittingCost, setSubmittingCost] = useState(false);
  const [settlementDetails, setSettlementDetails] = useState<OrderSettlementDto | null>(null);
  // Every recognized result comes from the invoice ledger; before the first
  // sales invoice the page shows the order's live cost-model figures.
  const hasLedgerSettlement = Boolean(settlementDetails?.entries.length);
  const [, setAuthRevision] = useState(0);

  const canEditOrderDetails = canEditCustomerOrderDetails(currentUser);
  const canManageOrderOperations = canManageCustomerOrderOperations(currentUser);
  const canViewOrderTransports = canReadTransportRoutes(currentUser);
  const canManageTransportStatus = canManageTransportRoutes(currentUser);
  const canEditCustomerOrderDocuments = canEditCustomerOrderDocumentFields(currentUser) && authService.canManageInvoiceImports();
  const canViewCustomerPayments = authService.canViewCustomerPayments();
  const canChangeOrderStatus = canChangeCustomerOrderStatus(currentUser);
  // UX mirror of requireCustomerOrderDeliveryRevert (ADMIN only); the backend is the gate.
  const canRevertDelivery = isAdminUser(currentUser);
  // UX mirror of requireCustomerOrderManualClose (ADMIN only); the backend is the gate.
  const canCloseOrderManually = isAdminUser(currentUser);
  const canManageOrderCosts = authService.canManageOrderCosts();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewSalesDocuments = authService.canViewSalesDocuments();
  const canViewCustomerSalePrices = authService.canViewCustomerSalePrices();
  const canEditSalesDocuments = authService.canEditCustomerSalePrices();
  const canViewMargin = authService.canViewMargin();
  // Sales rep: planned margin of his own order only (decision 2026-09-24).
  const canViewPlannedMargin = authService.canViewCustomerOrderPlannedMargin();
  const canViewCustomerOrderFallbackCosts = authService.canViewCustomerOrderFallbackCosts();
  const canEditCustomerOrderFallbackCosts = authService.canEditCustomerOrderFallbackCosts();
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canEditCosts = canEditCustomerOrderFallbackCosts;
  const canEditProducts = canEditCustomerOrderProducts(currentUser);
  const canViewCustomerOrderFinancialCosts = canManageOrderCosts || canViewMargin;
  const canViewCustomerOrderCostSection = canViewCustomerOrderFinancialCosts || canViewCustomerOrderFallbackCosts;
  const canManageNotificationAssignments = canManageOrderNotificationAssignments(currentUser);
  const canViewOrderTasks = canUseInternalTasks(currentUser);
  const canViewCommunications = canViewOrderCommunications(currentUser);
  const canDeleteTransportLink = canDeleteCustomerOrderTransportLink(currentUser);
  const {
    submitting: statusSubmitting,
    submitStatusChange,
  } = useOrderStatusChangeWithConfirmation({
    orderId: id,
    confirmation: { modal, t },
    onSuccess: async (values, result) => {
      feedback.success(t('orders:orderDetail.messages.statusUpdateSuccess', {
        orderNumber: orderDisplayNumber,
        status: t(`orders:statusOptions.${values.status}`),
      }));
      if (result.operationalWarning) {
        feedback.warning(result.operationalWarning);
      }
      setStatusModalVisible(false);
      setConfirmDialogOpen(false);
      setDeliverDialogOpen(false);
      setViewedStepKey(null);
      if (values.status === 'CONFIRMED') setOfferAcceptance(emptyOfferAcceptance());
      statusForm.resetFields();
      setConfirmationNotice(null);
      await Promise.all([
        refreshOrder(),
        canViewOrderTransports ? refreshOrderTransports() : Promise.resolve(),
        refreshSupplierDraft(),
      ]);
      // Notices go out after the commit, in the background: read the log
      // again a moment later so „Aktywność” and the hints name who got them.
      window.setTimeout(() => processData.refresh(), 2500);
    },
    onError: (error: any) => {
      const showRefusal = () => feedback.error(getOrderErrorMessage(error, t, 'orders:orderDetail.messages.statusUpdateError', {
        orderNumber: orderDisplayNumber,
      }), { sticky: isStickyOrderStatusRefusal(error) });
      if (!isOrderStatusConflictError(error)) {
        showRefusal();
        return;
      }
      // The card showed an outdated status (someone else already moved the order). Reload so
      // the card and the status list match the saved status, and say what happened instead of
      // „nie można zmienić statusu” beside a card that now shows the new status.
      setStatusModalVisible(false);
      const shownStatus = order?.status;
      void refreshOrder().then(
        (fresh) => {
          const conflict = getOrderStatusConflictMessage(t, shownStatus, fresh?.status);
          if (conflict) feedback.warning(conflict);
          else showRefusal();
        },
        showRefusal,
      );
    },
  });
  // Backend-computed reasons (GET /orders/:id → blockedActions). Buttons stay
  // visible but disabled with the reason; `warning: true` rows are advisory.
  const orderBlockedActions = order?.blockedActions || [];
  const orderHardBlocks = orderBlockedActions.filter((row) => !row.warning);
  // The status dialog shows only the "Dostarczone" advisories; WP12 B1 also
  // marks transports / unreleased WZ / loaded cargo as warnings of the delete
  // and the line removal (their commands ask for a confirmation).
  const orderDeliveryWarnings = orderBlockedActions.filter((row) => row.warning && row.action === 'deliver');
  // WP12 B1 "Anuluj i ukryj": offered where a hard delete refusal allows it.
  const canArchiveOrder = Boolean(order && !order.archivedAt && canDeleteCustomerOrders(currentUser)
    && orderHardBlocks.some((row) => row.action === 'delete' && row.archiveAvailable));
  const handleArchiveOrder = async () => {
    if (!order) return;
    try {
      const archived = await archiveOrder(order.id, { modal, t });
      if (!archived) return;
      feedback.success(t('orders:archive.success', { orderNumber: orderDisplayNumber }));
      await refreshOrder();
    } catch (error) {
      feedback.error(getOrderErrorMessage(error, t, 'orders:archive.error', { orderNumber: orderDisplayNumber }));
    }
  };
  const handleUnarchiveOrder = async () => {
    if (!order) return;
    try {
      await unarchiveOrder(order.id);
      feedback.success(t('orders:archive.unarchiveSuccess', { orderNumber: orderDisplayNumber }));
      await refreshOrder();
    } catch (error) {
      feedback.error(getOrderErrorMessage(error, t, 'orders:archive.unarchiveError', { orderNumber: orderDisplayNumber }));
    }
  };
  const orderBlockedFor = (action: string) => findBlockedAction(orderHardBlocks, action);
  const orderBlockedTooltip = (action: string) => {
    const blocked = orderBlockedFor(action);
    return blocked ? getBlockedActionMessage(blocked, t, 'orders', ORDER_ERROR_CODE_KEYS) : undefined;
  };
  const [accountManagers, setAccountManagers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const { options: suppliers } = useSupplierOptions();
  const customerConfirmation = order?.customerConfirmation;
  // Customer e-mails (offer, status, confirmation): ADMIN, Operacje, Handlowiec on his own ZK.
  const canSendCustomerEmails = canSendCustomerEmail(currentUser);
  const canDownloadOrderPdf = canDownloadCustomerOrderPdf(currentUser, canViewSalesDocuments);
  const canSendCustomerConfirmation = Boolean(canSendCustomerEmails && customerConfirmation?.canSend);
  const customerConfirmationDisabledReason = getCustomerConfirmationIssueMessage(
    customerConfirmation?.disabledReason,
    t,
  );
  // Not while the order is a draft: the mockup header has no such notice there, the confirmation is steps away.
  const showCustomerConfirmationBlockedReason = Boolean(
    canSendCustomerEmails && !canSendCustomerConfirmation && customerConfirmationDisabledReason
    && order?.status !== 'NEW_INQUIRY',
  );
  const customerStatusEmail = order?.customerStatusEmail;
  const canSendCustomerStatusEmail = Boolean(canSendCustomerEmails && customerStatusEmail?.canSend);
  const customerStatusEmailDisabledReason = getCustomerStatusEmailIssueMessage(
    customerStatusEmail?.disabledReason,
    t,
  );
  const useManualNumberingValue = Form.useWatch('useManualNumbering', editForm);

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  useEffect(() => {
    if (canViewOrderTransports) {
      refreshOrderTransports();
    } else {
      setOrderTransports({ transports: [], productRealization: [] });
    }
    fetchMarginHistory();
    fetchSettlementDetails();
    fetchOrderTasks();
    if (canEditOrderDetails) {
      fetchAccountManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEditOrderDetails, canManageOrderOperations, canViewMargin, canViewOrderTransports, canViewSalesDocuments]);

  // „Faktury sprzedaży” of the ZK (sales document readers): reloaded with the order.
  const [salesInvoices, setSalesInvoices] = useState<OrderSalesInvoice[] | null>(null);
  // A failed load is shown on the card (not an empty list): the reader must
  // not take „no table” for „no invoice”.
  const [salesInvoicesFailed, setSalesInvoicesFailed] = useState(false);
  // Advance / settlement invoices of the ZK against its value (same response).
  const [advanceBalance, setAdvanceBalance] = useState<AdvanceInvoiceBalance | null>(null);
  const loadSalesInvoices = useCallback(async () => {
    if (!id || !canViewSalesDocuments) {
      setSalesInvoices(null);
      setAdvanceBalance(null);
      setSalesInvoicesFailed(false);
      return;
    }
    try {
      const loaded = await fetchOrderSalesInvoices(id);
      setSalesInvoices(loaded.invoices);
      setAdvanceBalance(loaded.advanceBalance);
      setSalesInvoicesFailed(false);
    } catch (error) {
      console.error('Error fetching order sales invoices:', error);
      setSalesInvoices(null);
      setSalesInvoicesFailed(true);
    }
  }, [id, canViewSalesDocuments]);
  const orderVersionKey = order ? `${order.status}|${order.updatedAt ?? ''}|${order.invoiceCoverage?.state ?? ''}` : '';
  useEffect(() => {
    void loadSalesInvoices();
  }, [loadSalesInvoices, orderVersionKey]);

  useEffect(() => {
    if (!orderError) {
      return;
    }

    feedback.error(t('orders:orderDetail.messages.fetchError'));
  }, [feedback, orderError, t]);


  const fetchMarginHistory = async () => {
    if (!id || !canViewMargin) return;

    try {
      setMarginHistory(await fetchOrderMarginHistory(id));
    } catch (error) {
      console.error('Error fetching margin history:', error);
    }
  };

  const fetchSettlementDetails = async () => {
    if (!id || !canViewMargin) {
      setSettlementDetails(null);
      return;
    }

    try {
      setSettlementDetails(await fetchOrderSettlement(id));
    } catch (error) {
      console.error('Error fetching settlement details:', error);
      setSettlementDetails(null);
    }
  };

  const refreshOrderFinancials = async () => {
    await refreshOrderFinanceReadModels({
      order: refreshOrder,
      marginHistory: fetchMarginHistory,
      settlementLedger: fetchSettlementDetails,
    });
  };

  // A draft deleted or an invoice withdrawn: the list, the order (status,
  // coverage), its finance read models and the process facts change.
  const handleSalesInvoicesChanged = () => {
    void loadSalesInvoices();
    void refreshOrderFinancials();
    processData.refresh();
  };

  const fetchOrderTasks = async () => {
    if (!id || !canViewOrderTasks) return;
    setOrderTasksLoading(true);
    try {
      const tasks = await internalTaskApi.getTasksByOrder(id);
      setOrderTasks(tasks);
    } catch (error) {
      console.error('Error fetching order tasks:', error);
    } finally {
      setOrderTasksLoading(false);
    }
  };

  async function refreshOrderTransports() {
    if (!id) return;
    setTransportLoading(true);
    try {
      setOrderTransports(await fetchOrderTransports(id));
    } catch (error: any) {
      console.error('Error fetching order transports:', error);
    } finally {
      setTransportLoading(false);
    }
  }

  const fetchAccountManagers = async () => {
    try {
      const response = await api.get('/users/options');
      setAccountManagers(response.data || []);
    } catch (error) {
      console.error('Error fetching account managers:', error);
    }
  };

  const normalizeUploadFileList = (event: any) => {
    if (Array.isArray(event)) {
      return event;
    }

    return event?.fileList || [];
  };

  const openTransportManagement = (cost?: Cost | null) => {
    if (cost?.transportId) {
      navigate(withTransportReturnContext(
        `/app/transports/${encodeURIComponent(cost.transportId)}/edit`,
        id,
      ));
      return;
    }

    setActiveTabKey('2');
  };

  const closeCostModal = () => {
    setCostModalVisible(false);
    setEditingCost(null);
    form.resetFields();
  };

  const openAddCostModal = () => {
    setEditingCost(null);
    form.resetFields();
    form.setFieldsValue({
      currency: order?.currency || 'PLN',
      document: [],
    });
    setCostModalVisible(true);
  };

  const openEditCostModal = (cost: Cost) => {
    if (isManagedTransportCost(cost)) {
      openTransportManagement(cost);
      return;
    }

    // If cost was entered in a foreign currency, show the original amount;
    // otherwise show the stored PLN amount directly.
    const displayAmount = cost.originalAmount != null && cost.originalCurrency && cost.originalCurrency !== 'PLN'
      ? cost.originalAmount
      : cost.amount;

    setEditingCost(cost);
    form.resetFields();
    form.setFieldsValue({
      currency: cost.originalCurrency || 'PLN',
      amount: displayAmount,
      description: cost.description,
      document: [],
    });
    setCostModalVisible(true);
  };

  const handleSubmitCost = async (values: any) => {
    if (!id) return;

    setSubmittingCost(true);

    try {
      await runOrderFinanceMutation(
        () => saveOrderCost({
          orderId: id,
          costId: editingCost?.id,
          category: editingCost?.category,
          values,
        }),
        refreshOrderFinancials,
      );

      const costName = describeCost(values?.category, values?.description);
      feedback.success(
        editingCost
          ? t('orders:orderDetail.messages.costUpdatedSuccess', { costName })
          : t('orders:orderDetail.messages.costAddedSuccess', { costName })
      );
      closeCostModal();
    } catch (error) {
      feedback.error(
        editingCost
          ? t('orders:orderDetail.messages.costUpdateError')
          : t('orders:orderDetail.messages.costAddError')
      );
    } finally {
      setSubmittingCost(false);
    }
  };

  const handleAddCost = async (values: any) => {
    await handleSubmitCost(values);
  };

  const handleDeleteCost = async (cost: Cost) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:orderDetail.deleteCostConfirmTitle'),
        content: t('orders:orderDetail.deleteCostConfirmDescription'),
        okText: t('orders:orderDetail.delete'),
        okType: 'danger',
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    try {
      const costName = describeCost(cost.category, cost.description);
      await runOrderFinanceMutation(
        () => deleteOrderCost(cost.id),
        refreshOrderFinancials,
      );
      feedback.success(t('orders:orderDetail.messages.costDeletedSuccess', { costName }));
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.costDeleteError', undefined, {
        costName: describeCost(cost.category, cost.description),
      }));
    }
  };

  const handleSendCustomerConfirmation = async () => {
    if (!id) return;
    setCustomerEmailSending(true);
    try {
      const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
      const response = await sendCustomerConfirmationEmail(id, {
        language,
        ...(customerEmailEditing
          ? { subject: customerEmailSubject, message: customerEmailMessage }
          : {}),
      });
      feedback.success(t('orders:orderDetail.customerConfirmationSentTo', { email: response.recipient }));
      setCustomerEmailModalVisible(false);
      setCustomerEmailEditing(false);
    } catch (error: any) {
      const serverCode = error?.response?.data?.code;
      const issueMessage = getCustomerConfirmationIssueMessage(serverCode, t, false);
      feedback.error(issueMessage || getApiErrorMessage(error, t, 'orders:orderDetail.customerConfirmationError'));
    } finally {
      setCustomerEmailSending(false);
    }
  };

  useEffect(() => {
    if (!customerEmailModalVisible || !id) {
      return;
    }
    let cancelled = false;
    setCustomerEmailPreviewLoading(true);
    setCustomerEmailPreview(null);
    setCustomerEmailDefaultPreview(null);
    setCustomerEmailSubject('');
    setCustomerEmailMessage('');
    setCustomerEmailEditing(false);
    const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
    previewCustomerConfirmationEmail(id, language)
      .then((preview) => {
        if (!cancelled) {
          setCustomerEmailPreview(preview);
          setCustomerEmailDefaultPreview(preview);
          setCustomerEmailSubject(preview.subject || '');
          setCustomerEmailMessage(preview.message || '');
        }
      })
      .catch(() => {
        if (!cancelled) feedback.error(t('orders:orderDetail.customerConfirmationPreviewError'));
      })
      .finally(() => {
        if (!cancelled) setCustomerEmailPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [customerEmailModalVisible, id, feedback, orderDocumentLanguage, t]);

  useEffect(() => {
    if (!customerEmailModalVisible || !customerEmailEditing || !id) {
      return;
    }
    if (!customerEmailSubject.trim() || !customerEmailMessage.trim()) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
      previewCustomerConfirmationEmailDraft(id, {
        language,
        subject: customerEmailSubject,
        message: customerEmailMessage
      })
        .then((preview) => {
          if (!cancelled) setCustomerEmailPreview(preview);
        })
        .catch(() => {
          if (!cancelled) feedback.error(t('orders:orderDetail.customerConfirmationPreviewError'));
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    customerEmailEditing,
    customerEmailMessage,
    customerEmailModalVisible,
    customerEmailSubject,
    id,
    feedback,
    orderDocumentLanguage,
    t
  ]);

  const handleSendCustomerStatusEmail = async () => {
    if (!id) return;
    setStatusEmailSending(true);
    try {
      const response = await sendCustomerStatusEmail(id, {
        ...(statusEmailEditing
          ? { subject: statusEmailSubject, message: statusEmailMessage }
          : {}),
      });
      const offer = statusEmailPurpose === 'offer';
      feedback.success(t(offer ? 'orders:process.offer.sentTo' : 'orders:orderDetail.customerStatusEmailSentTo', { email: response.recipient }));
      setStatusEmailModalVisible(false);
      setStatusEmailEditing(false);
      setStatusEmailPurpose('status');
      processData.refresh();
      // The offer really went out: only now the order becomes „Oferta wysłana”.
      if (offer && order?.status === 'NEW_INQUIRY' && canChangeOrderStatus) {
        await submitStatusChange({
          status: 'QUOTE_SENT',
          notes: t('orders:process.offer.sentNote', { email: response.recipient }),
        });
      }
    } catch (error: any) {
      const serverCode = error?.response?.data?.code;
      const issueMessage = getCustomerStatusEmailIssueMessage(serverCode, t, false);
      feedback.error(issueMessage || getApiErrorMessage(error, t, 'orders:orderDetail.customerStatusEmailError'));
    } finally {
      setStatusEmailSending(false);
    }
  };

  useEffect(() => {
    if (!statusEmailModalVisible || !id) {
      return;
    }
    let cancelled = false;
    setStatusEmailPreviewLoading(true);
    setStatusEmailPreview(null);
    setStatusEmailDefaultPreview(null);
    setStatusEmailSubject('');
    setStatusEmailMessage('');
    setStatusEmailEditing(false);
    previewCustomerStatusEmail(id)
      .then((preview) => {
        if (!cancelled) {
          setStatusEmailPreview(preview);
          setStatusEmailDefaultPreview(preview);
          if (statusEmailPurpose === 'offer' && order) {
            // The offer text replaces the status wording; the draft preview
            // below renders it exactly as the customer will get it.
            const offerText = buildOfferEmailText({ orderNumber: getDisplayOrderNumber(order) }, t);
            setStatusEmailSubject(offerText.subject);
            setStatusEmailMessage(offerText.message);
            setStatusEmailEditing(true);
          } else {
            setStatusEmailSubject(preview.subject || '');
            setStatusEmailMessage(preview.message || '');
          }
        }
      })
      .catch(() => {
        if (!cancelled) feedback.error(t('orders:orderDetail.customerStatusEmailPreviewError'));
      })
      .finally(() => {
        if (!cancelled) setStatusEmailPreviewLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusEmailModalVisible, id, feedback, t, statusEmailPurpose]);

  useEffect(() => {
    if (!statusEmailModalVisible || !statusEmailEditing || !id) {
      return;
    }
    if (!statusEmailSubject.trim() || !statusEmailMessage.trim()) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      previewCustomerStatusEmailDraft(id, {
        subject: statusEmailSubject,
        message: statusEmailMessage
      })
        .then((preview) => {
          if (!cancelled) setStatusEmailPreview(preview);
        })
        .catch(() => {
          if (!cancelled) feedback.error(t('orders:orderDetail.customerStatusEmailPreviewError'));
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    id,
    feedback,
    statusEmailEditing,
    statusEmailMessage,
    statusEmailModalVisible,
    statusEmailSubject,
    t
  ]);

  const handleUpdateStatus = async (values: OrderStatusFormValues) => {
    const startsCycle = Boolean(order) && order?.isQuote !== true
      && startsCustomerOrderConfirmationCycle(order?.status, values.status);
    await submitStatusChange(startsCycle && confirmationNotice
      ? { ...values, confirmationNotice }
      : values);
  };

  const handleDownloadOrderDocument = async (language: OrderDocumentLanguage = orderDocumentLanguage) => {
    if (!id || !order || !canDownloadOrderPdf) return;

    const conversionChoice = await promptOrderPdfConversionUnit(modal, t);

    if (!conversionChoice) return;

    setOrderDocumentDownloading(true);
    try {
      const orderBlob = await downloadOrderPdf(
        id,
        language,
        conversionChoice === 'none' ? undefined : conversionChoice,
      );
      const url = window.URL.createObjectURL(new Blob([orderBlob]));
      const link = document.createElement('a');
      const filePrefix = language === 'pl' ? 'zamowienie' : 'order';
      link.href = url;
      link.setAttribute('download', `${buildSafePdfFileName(filePrefix, getDisplayOrderNumber(order))}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(t('orders:orderDetail.orderDocumentDownloaded', { orderNumber: orderDisplayNumber }));
    } catch (error) {
      feedback.error(t('orders:orderDetail.orderDocumentDownloadFailed', { orderNumber: orderDisplayNumber }));
    } finally {
      setOrderDocumentDownloading(false);
    }
  };

  const { handleUnlinkTransport, handleTransportStatusChange } = createOrderTransportActions({
    id, modal, t, feedback, orderTransports,
    refreshOrderTransports: async () => { await Promise.all([refreshOrderTransports(), refreshOrder()]); },
    refreshOrderFinancials,
    orderLabel: orderDisplayNumber,
    canViewTransportCosts: authService.canViewTransportCosts(),
  });

  const handleConvertQuote = async () => {
    if (!id) return;

    try {
      await convertQuoteToOrder(id);
      feedback.success(t('orders:orderDetail.quoteConverted', { orderNumber: orderDisplayNumber }));
      await refreshOrder();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.quoteConvertError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    }
  };


  const startEditing = (field: OrderDetailEditableField) => {
    if (!order) return;

    editForm.resetFields();
    setEditingField(field);
    editForm.setFieldsValue(getOrderDetailInitialValues(order, field));
  };

  const cancelEditing = (field: OrderDetailEditableField) => {
    if (editingField !== field) return;

    setEditingField(null);
    editForm.resetFields();
  };

  const saveField = async (field: OrderDetailEditableField) => {
    if (!id) return;

    try {
      const values = editForm.getFieldsValue(true);
      await editForm.validateFields(getOrderDetailFieldNames(field, values));
      setSavingField(field);
      const updateData = buildOrderDetailUpdateData(field, values);
      if (field === 'forecastMonth' && order && isForecastMonthUpdateNoop(order, updateData)) {
        cancelEditing(field);
        return;
      }

      // WP1 part 2: an invoiced order asks for a confirmed reason when an
      // invoice-relevant field changes; a cancelled dialog keeps the editor open.
      if (!await updateOrder(id, updateData, { modal, t })) return;
      feedback.success(t('orders:orderDetail.messages.updateSuccess', {
        orderNumber: order ? getDisplayOrderNumberAfterUpdate(order, updateData) : orderDisplayNumber,
      }));
      cancelEditing(field);
      await refreshOrder();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  // Decision 2026-10-04: from the transport dialog of a self-pickup order.
  const switchToOurDelivery = async (): Promise<boolean> => {
    if (!id) return false;
    try {
      if (!await updateOrder(id, buildOrderDetailUpdateData('deliveryMode', { deliveryMode: 'OUR_DELIVERY' }), { modal, t })) {
        return false;
      }
      feedback.success(t('transport:attach.pickupSwitched', { number: orderDisplayNumber }));
      // No refreshOrder(): its loading state replaces the page with a spinner
      // and would close the transport dialog the operator is working in.
      setOrder((current) => (current ? { ...current, deliveryMode: 'OUR_DELIVERY' } : current));
      return true;
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
      return false;
    }
  };

  const saveOrderNumber = async () => {
    if (!id) return;
    try {
      const values = numberForm.getFieldsValue(true);
      await numberForm.validateFields(getOrderDetailFieldNames('manualNumbering', values));
      setSavingField('manualNumbering');
      const updateData = buildOrderDetailUpdateData('manualNumbering', values);
      if (!await updateOrder(id, updateData, { modal, t })) return;
      feedback.success(t('orders:orderDetail.messages.updateSuccess', {
        orderNumber: order ? getDisplayOrderNumberAfterUpdate(order, updateData) : orderDisplayNumber,
      }));
      setNumberModalOpen(false);
      await refreshOrder();
    } catch (error: any) {
      if (error?.errorFields) return;
      const taken = getOrderNumberTaken(error);
      if (taken) {
        setNumberTaken(taken);
        return;
      }
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const getNumberModalInitialValues = (): Record<string, unknown> => {
    if (!order) return {};
    const init = getOrderDetailInitialValues(order, 'manualNumbering');
    if (!order.useManualNumbering) {
      init.manualOrderNumber = getDisplayOrderNumber(order);
    }
    return init;
  };

  const renderEditableField = (
    field: OrderDetailEditableField,
    value: React.ReactNode,
    renderComponent: () => React.ReactNode,
    canEditField = canEditOrderDetails
  ) => {
    if (!canEditField) return value;

    const isEditing = editingField === field;

    if (isEditing) {
      return (
        <div style={{ display: 'grid', gap: 10, width: '100%' }}>
          {renderComponent()}
          <Space wrap>
            <Button
              icon={<SaveOutlined />}
              size="small"
              type="primary"
              loading={savingField === field}
              onClick={() => saveField(field)}
            >
              {t('common:save')}
            </Button>
            <Button
              icon={<CloseOutlined />}
              size="small"
              onClick={() => cancelEditing(field)}
              disabled={savingField === field}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>{value}</div>
        <Tooltip title={t('orders:orderDetail.edit')}>
          <Button
            aria-label={t('orders:orderDetail.edit')}
            icon={<EditOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={() => startEditing(field)}
            disabled={Boolean(editingField && editingField !== field)}
            style={{ flex: '0 0 auto', marginTop: -3, color: 'var(--app-text-secondary)' }}
          />
        </Tooltip>
      </div>
    );
  };

  // A refresh keeps the card (and its open dialogs) on screen; only the first load spins.
  if (loading && !order) {
    return <Spin size="large" tip={t('orders:orderDetail.loading')} style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (orderError) {
    return (
      <Alert
        message={t('orders:orderDetail.messages.fetchError')}
        type="error"
        showIcon
        action={<Button size="small" onClick={() => { void refreshOrder().catch(() => undefined); }}>{t('common:retry')}</Button>}
      />
    );
  }

  if (!order) {
    return <div>{t('orders:orderDetail.orderNotFound')}</div>;
  }

  const costColumns = [
    {
      title: t('orders:orderDetail.category'),
      dataIndex: 'category',
      key: 'category',
      render: (cat: string) => t(`orders:costCategories.${cat}`),
    },
    {
      title: t('orders:orderDetail.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <CurrencyAmount
          amount={record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null ? record.originalAmount : amount}
          currency={record.originalCurrency || 'PLN'}
          plnAmount={amount}
          rateDate={record.incurredAt}
        />
      ),
    },
    {
      title: t('orders:orderDetail.purchaseInvoice'),
      key: 'purchaseInvoice',
      render: (_: unknown, record: Cost) => {
        if (record.category !== 'PRODUCT_COST') {
          return '-';
        }

        if (!record.purchaseInvoiceNumber && !record.purchaseInvoiceDate) {
          return '-';
        }

        return (
          <div>
            {record.purchaseInvoiceNumber && (
              <div>
                {record.purchaseInvoiceNumber}
              </div>
            )}
            {record.purchaseInvoiceDate && (
              <div style={{ fontSize: '0.9em', color: 'var(--app-text-secondary)' }}>
                {formatDateEuropean(record.purchaseInvoiceDate)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders:orderDetail.costDescription'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('orders:orderDetail.addedBy'),
      dataIndex: ['addedBy', 'name'],
      key: 'addedBy',
    },
    {
      title: t('orders:orderDetail.date'),
      dataIndex: 'incurredAt',
      key: 'incurredAt',
      render: (date: string) => formatDateEuropean(date),
    },
    ...((canEditCosts || canEditTransportCosts) ? [{
      title: t('orders:orderDetail.actions'),
      key: 'actions',
      width: 140,
      render: (_: any, record: Cost) => (
        <Space size="small">
          <Button
            size="small"
            icon={isManagedTransportCost(record) ? <TruckOutlined /> : <EditOutlined />}
            disabled={
              (isManagedTransportCost(record) && !canEditTransportCosts)
              || (!isManagedTransportCost(record) && !canEditCustomerOrderFallbackCosts)
              || (!isManagedTransportCost(record) && !isManualCustomerOrderCost(record))
            }
            onClick={() => (
              isManagedTransportCost(record)
                ? openTransportManagement(record)
                : openEditCostModal(record)
            )}
          >
            {isManagedTransportCost(record) ? t('transport:editTransport') : t('common:edit')}
          </Button>
          {isManualCustomerOrderCost(record) && canEditCustomerOrderFallbackCosts && (
            <Tooltip title={t('orders:orderDetail.delete')}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={t('orders:orderDetail.delete')}
                onClick={() => handleDeleteCost(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    }] : []),
  ];
  const operationalCostColumns = costColumns.filter((column: any) => column.key !== 'purchaseInvoice');
  const purchaseInvoiceCostColumns = costColumns;

  const customerName = order.customer?.name || t('orders:orderDetail.archivedCustomer');
  const createdByName = order.createdBy?.name || t('orders:orderDetail.deletedUser');
  const orderCosts = Array.isArray(order.costs) ? order.costs : [];
  const operationalCosts = orderCosts.filter(isOperationalCustomerOrderCost);
  const customerFallbackCosts = orderCosts.filter(isManualCustomerOrderCost);
  const customerOrderCostRows = canViewCustomerOrderFinancialCosts
    ? operationalCosts
    : customerFallbackCosts;
  const purchaseInvoiceCosts = orderCosts.filter(isPurchaseInvoiceCost);
  const orderStatusHistory = Array.isArray(order.statusHistory) ? order.statusHistory : [];
  // Status history plus the confirmation draft ZD entries (decision
  // 2026-10-04), newest first like the status history itself.
  const processNotifications = summarizeOrderNotifications(processData.notifications);
  const notificationRecipients = (entry: typeof processNotifications.entries[number]) => (entry.toCustomer
    ? t('orders:process.activity.customer', { recipients: entry.recipients.join(', ') })
    : entry.recipients.join(', '));
  const orderActivityHistory: any[] = [
    ...orderStatusHistory,
    // Who got which e-mail about this ZK (notification log).
    ...processNotifications.entries.map((entry) => ({
      id: entry.id,
      title: t('orders:process.activity.notification', { event: t(`notifications:eventTypes.${entry.eventType}`, entry.eventType) }),
      changedAt: entry.at,
      changedBy: { name: t('orders:process.activity.system') },
      notificationLog: entry.logs.find((log) => log.content) ?? null,
      notes: [
        entry.recipients.length ? t('orders:process.activity.sentTo', { recipients: notificationRecipients(entry) }) : null,
        entry.failed.length ? t('orders:process.activity.failed', { recipients: entry.failed.join(', ') }) : null,
        entry.subject ? t('orders:process.activity.subject', { subject: entry.subject }) : null,
      ].filter(Boolean).join(' '),
    })),
    ...buildSupplierDraftHistoryEntries(supplierDraft, t).map((entry) => ({
      id: entry.id,
      title: entry.title,
      supplierOrderId: entry.supplierOrderId,
      changedAt: entry.at,
      changedBy: entry.author ? { name: entry.author } : null,
      notes: entry.notes,
    })),
  ].sort((a: any, b: any) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
  // Margin history is only rendered for canViewMargin, so prices may appear here.
  const formatMarginHistoryLineImage = (image: OrderMarginHistoryLineImage | null) => {
    if (!image) return t('orders:orderDetail.marginHistoryLineNone');
    const quantity = image.saleQuantity ?? image.quantity;
    const unit = image.saleUnit ?? image.unit;
    const quantityLabel = quantity === null
      ? '—'
      : `${formatLocalizedQuantity(quantity, i18n.language)} ${unit ? formatUnit(unit, i18n.language) : ''}`.trim();
    const priceLabel = image.unitPrice === null
      ? '—'
      : formatCurrencyLocalized(image.unitPrice, image.currency || order.currency || 'PLN', i18n.language);
    return `${quantityLabel} × ${priceLabel}`;
  };
  // Money is stored in PLN (migration 054). Display stored values directly.
  const toDisplayAmount = (amount: number | null | undefined) => Number(amount ?? 0);
  const getCostAmountInPln = (cost: any) => toDisplayAmount(cost?.amount);
  const revenueDisplayAmount = toDisplayAmount(order.revenue);
  const totalCostDisplayAmount = toDisplayAmount(order.totalCost);
  const marginDisplayAmount = toDisplayAmount(order.margin);
  // One home for how the order margin reads (orderMarginSummary.ts).
  const marginSummary = getOrderMarginSummary(order);
  const marginTone = marginSummary.tone;
  const marginStatusKey = marginSummary.statusKey;
  const marginPercentLabel = marginSummary.percentUnavailableKey
    ? t(marginSummary.percentUnavailableKey)
    : formatLocalizedPercent(marginSummary.percent, i18n.language);
  const isFinancialCompletenessPending = canShowOrderFinancialCompletenessStatus(
    canViewCustomerSalePrices,
    order.financialCompleteness,
  );
  const salePricesIncomplete = canShowOrderSalePricesIncompleteHint(
    canViewCustomerSalePrices,
    order.financialCompleteness,
  );
  const customerPaymentStatus = order.customerPaymentStatus || 'PENDING';
  const customerPaymentDueDate = order.customerPaymentDueDate ? dayjs(order.customerPaymentDueDate) : null;
  const isCustomerPaymentOverdue = Boolean(
    customerPaymentStatus !== 'PAID' &&
    customerPaymentDueDate &&
    customerPaymentDueDate.isBefore(dayjs(), 'day')
  );

  // ---- ZK card by the mockup (decision 2026-10-04) ----
  const processPosition = getOrderProcessPosition(order.status, orderStatusHistory);
  const viewedStep: OrderProcessStepKey = viewedStepKey && canOpenOrderProcessStep(processPosition, viewedStepKey)
    ? viewedStepKey
    : processPosition.current;
  const viewedReadOnly = isOrderProcessStepReadOnly(processPosition, viewedStep);
  const processFacts = getOrderProcessFacts(processData.stockStatus, processData.warehouse, processData.wzDocuments);
  const marginVisibility = getOrderMarginVisibility({ canViewMargin, canViewPlannedMargin });
  const marginFigure = getOrderMarginFigure({
    order,
    settlement: settlementDetails,
    allIssued: processFacts.allIssued,
    costProvisional: processData.warehouse?.finance?.costProvisional === true,
    incompleteLines: getOrderMarginIncompleteLines({
      products: order.productsLoaded ? order.products : [],
      planLines: processData.warehouse?.finance?.plan?.lines ?? null,
      fifoLines: processFifo.lines,
      displayName: (product) => getOrderProductDisplayName(product as OrderProduct, product.name),
    }),
  });
  const canManageWz = canManageWZDocuments(currentUser);
  const canChangeStatusNow = canChangeOrderStatus && !orderBlockedFor('changeStatus');
  const formatProcessQuantity = (value: number, unit: string) =>
    `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit, i18n.language)}`;
  const pickup = order.deliveryMode === 'CUSTOMER_PICKUP';
  // The Szkic is edited on the card while the order is a draft; a later step shows it read-only.
  const draftEditable = viewedStep === 'DRAFT' && !viewedReadOnly && order.status === 'NEW_INQUIRY' && canEditOrderDetails;
  const draftReadiness = getOrderDraftReadiness(draftEditor.draft.lines, canViewCustomerSalePrices);
  const processCopy = {
    pickup,
    partiallyDelivered: order.status === 'PARTIALLY_DELIVERED',
    allInStock: Boolean(processData.stockStatus) && processFacts.missing.length === 0,
    confirmationSent: processNotifications.confirmationSent === true,
  };
  const stepBar = getOrderStepBar(viewedStep, {
    status: order.status,
    shortageNotice: shortageNoticeFor(processNotifications, order.status),
    pickup,
    // „Prosto z fabryki” orders the whole quantity anyway: the warehouse shortage is not a hint there.
    factoryDirect: order.fulfillmentSource === 'FACTORY_DIRECT',
    missing: order.fulfillmentSource === 'FACTORY_DIRECT'
      ? []
      : processFacts.missing.map((line) => `${withLineDimensions(line.name, order.products.find((product) => product.id === line.orderProductId))} ${formatProcessQuantity(line.quantity, line.unit)}`),
    ...getOrderStepBarOrderFacts({
      order,
      canViewSalePrices: canViewCustomerSalePrices,
      canViewTransports: canViewOrderTransports,
      transports: orderTransports.transports,
    }),
    // On the editable Szkic the lines being edited decide „Stwórz ofertę”.
    ...(draftEditable ? {
      lineCount: draftReadiness.lineCount,
      linesWithoutPrice: draftReadiness.linesWithoutPrice,
      draftPendingRow: Boolean(draftEditor.pendingRow?.line),
    } : {}),
    offerAccepted: offerAcceptance.accepted,
    offerAcceptanceDate: Boolean(offerAcceptance.date),
    allIssued: processFacts.allIssued,
    anyIssued: processFacts.anyIssued,
    can: {
      changeStatus: canChangeStatusNow,
      issueWz: canManageWz,
      salesInvoice: authService.canManageInvoiceImports(),
    },
  });
  const stepContext: OrderStepContext = {
    order,
    readOnly: viewedReadOnly,
    caps: {
      canViewSalePrices: canViewCustomerSalePrices,
      canViewSalesDocuments,
      canDownloadOrderPdf,
      canViewMargin,
      canViewPlannedMargin,
      canReadWarehouse,
      canViewWarehouseCosts: canReadWarehouse && authService.canViewWarehouseDocumentPurchasePrices(),
      canManageWz,
      canChangeStatus: canChangeStatusNow,
      canManageSalesInvoices: authService.canManageInvoiceImports(),
      canViewCustomerPayments,
      canViewTransports: canViewOrderTransports,
      canViewTransportCosts: authService.canViewTransportCosts(),
      canManageTransports: canManageOrderOperations && canViewOrderTransports,
      canEditDeliveryMode: canEditCustomerOrderDeliveryMode(currentUser),
      canSendCustomerConfirmation,
    },
    stockStatus: processData.stockStatus,
    warehouse: processData.warehouse,
    wzDocuments: processData.wzDocuments,
    facts: processFacts,
    margin: marginFigure,
    marginVisibility,
    settlement: settlementDetails,
    transports: orderTransports.transports,
    transportsLoading: transportLoading,
    deliverySchedule: processData.deliverySchedule,
    fifoLines: processFifo.lines,
    fifoLoading: processFifo.loading,
    fifoFailed: processFifo.failed,
    notifications: processNotifications,
  };

  // A toast belongs to the action that raised it: a new step or action closes the old (non-sticky) ones.
  const clearStaleToasts = clearCardToasts;

  // „Zapisz szkic” (and the save before „Stwórz ofertę”): header + lines through the order owners.
  const saveDraft = async (patch?: Parameters<typeof draftEditor.save>[0]): Promise<boolean> => {
    feedback.clear(); // the save-error toast is replaced by the result below (same key)
    try {
      const result = await draftEditor.save(patch);
      if (result.status === 'invalid') {
        // One message for the first missing condition; it closes itself and on the next attempt.
        feedback.error(t(result.problemKey), { key: ORDER_DRAFT_SAVE_ERROR_KEY });
        return false;
      }
      feedback.message.destroy(ORDER_DRAFT_SAVE_ERROR_KEY);
      if (result.status === 'cancelled') return false;
      if (result.status === 'saved') {
        feedback.success(t('orders:process.draft.saved', { orderNumber: orderDisplayNumber }));
        await refreshOrderFinancials();
        processData.refresh();
      }
      return true;
    } catch (error) {
      feedback.error(getOrderProductSaveErrorMessage(t, error, t('orders:process.draft.saveError', { orderNumber: orderDisplayNumber })), { key: ORDER_DRAFT_SAVE_ERROR_KEY });
      return false;
    }
  };

  // Leaving the editable Szkic with unsaved edits asks first (they would be lost).
  const confirmLeaveDraft = (onLeave: () => void) => {
    if (!draftEditable || !draftEditor.dirty) {
      onLeave();
      return;
    }
    modal.confirm({
      title: t('orders:process.draft.leaveTitle'),
      content: t('orders:process.draft.leaveContent'),
      okText: t('orders:process.draft.leaveOk'),
      cancelText: t('common:cancel'),
      onOk: () => {
        draftEditor.discard();
        onLeave();
      },
    });
  };

  // A ZK with an advance invoice is invoiced by the settlement invoice
  // (decision 2026-11-02-zaliczka-bez-przychodu), so the form opens on that kind.
  const newSalesInvoiceUrl = () => `/app/sales-invoices/new?orderId=${order.id}${order.hasAdvanceInvoice ? '&kind=SETTLEMENT' : ''}`;

  const handleStepPrimary = async (action: OrderStepPrimaryAction) => {
    clearStaleToasts();
    if (action.kind === 'openOffer') {
      if (draftEditable && draftEditor.dirty && !await saveDraft()) return;
      setViewedStepKey('OFFER');
      window.scrollTo({ top: 0 });
    } else if (action.kind === 'status') {
      void handleUpdateStatus({ status: action.status });
    } else if (action.kind === 'confirm') {
      if (order.paymentMode === 'PREPAYMENT' && draftRights.customerPayments && !order.prepaymentPaidAt) {
        setPrepaymentGateOpen(true);
        return;
      }
      setConfirmDialogOpen(true);
    } else if (action.kind === 'issueWz') {
      setWzIssueOpen(true);
    } else if (action.kind === 'deliver') {
      setDeliverDialogOpen(true);
    } else if (action.kind === 'salesInvoice') {
      navigate(newSalesInvoiceUrl());
    }
  };

  // „Utwórz WZ i przejdź do dostawy”: after a released WZ the order moves to
  // „W dostawie” (a later WZ keeps the status). Nothing moves for a draft WZ.
  const handleWzIssued = async (result: WzIssueResult) => {
    processData.refresh();
    const nextStatus = getStatusAfterWzIssued(order.status, result.released);
    if (nextStatus && canChangeStatusNow) {
      await submitStatusChange({
        status: nextStatus,
        notes: t('orders:process.wzIssuedNote', { number: result.wzNumber || '' }),
      });
      return;
    }
    await refreshOrderFinancials();
  };

  const handleDeliveryModeChange = async (mode: OrderDeliveryMode) => {
    // Picking a warehouse delivery also takes the order back from „prosto z fabryki”.
    const leavingFactory = order.fulfillmentSource === 'FACTORY_DIRECT';
    if (!id || (!leavingFactory && (order.deliveryMode === mode || (!order.deliveryMode && mode === 'OUR_DELIVERY')))) return;
    setSavingDeliveryMode(true);
    try {
      const payload = {
        ...buildOrderDetailUpdateData('deliveryMode', { deliveryMode: mode }),
        ...(leavingFactory ? { fulfillmentSource: 'WAREHOUSE' } : {}),
      };
      if (!await updateOrder(id, payload, { modal, t })) return;
      feedback.success(t('orders:process.ready.modeSaved', {
        orderNumber: orderDisplayNumber,
        mode: t(`orders:deliveryMode.options.${mode}`),
      }));
      await refreshOrderFinancials();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingDeliveryMode(false);
    }
  };

  // „Wpłata z proformy” (decision 2026-10-27): only the day. From „Oznacz wpłatę” confirming the ZK stays the
  // next, separate action; from the „Klient potwierdził” gate the confirmation dialog opens right after.
  const handlePrepaymentPaidSave = async (paidAt: string | null, { thenConfirm = false } = {}) => {
    if (!id) return;
    setSavingPrepayment(true);
    try {
      if (!await updateOrder(id, { prepaymentPaidAt: paidAt }, { modal, t })) return;
      if (thenConfirm) {
        setPrepaymentGateOpen(false);
        setConfirmDialogOpen(true);
      }
      feedback.success(paidAt
        ? t('orders:paymentMode.paidDialog.saved', { orderNumber: orderDisplayNumber, date: formatDateEuropean(paidAt) })
        : t('orders:paymentMode.paidDialog.cleared', { orderNumber: orderDisplayNumber }));
      setPrepaymentDialogOpen(false);
      await refreshOrderFinancials();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingPrepayment(false);
    }
  };

  const handleFactoryDirect = async () => {
    if (!id || order.fulfillmentSource === 'FACTORY_DIRECT') return;
    setSavingDeliveryMode(true);
    try {
      if (!await updateOrder(id, { fulfillmentSource: 'FACTORY_DIRECT' }, { modal, t })) return;
      feedback.success(t('orders:process.ready.factorySaved', { orderNumber: orderDisplayNumber }));
      await refreshOrderFinancials();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingDeliveryMode(false);
    }
  };

  const headerMenuItems: NonNullable<MenuProps['items']> = [
    ...(canDownloadOrderPdf ? [
      { key: 'pdf-pl', icon: <FilePdfOutlined />, label: t('orders:process.menu.pdfPl') },
      { key: 'pdf-en', icon: <FilePdfOutlined />, label: t('orders:process.menu.pdfEn') },
    ] : []),
    ...(canSendCustomerEmails ? [{
      key: 'customer-confirmation',
      icon: <MailOutlined />,
      disabled: !canSendCustomerConfirmation,
      label: canSendCustomerConfirmation
        ? t('orders:orderDetail.sendCustomerConfirmation')
        : <Tooltip title={customerConfirmationDisabledReason}>{t('orders:orderDetail.sendCustomerConfirmation')}</Tooltip>,
    }] : []),
    ...(canSendCustomerEmails ? [{
      key: 'status-email',
      icon: <MailOutlined />,
      disabled: !canSendCustomerStatusEmail,
      label: canSendCustomerStatusEmail
        ? t('orders:orderDetail.sendCustomerStatusEmail')
        : <Tooltip title={customerStatusEmailDisabledReason}>{t('orders:orderDetail.sendCustomerStatusEmail')}</Tooltip>,
    }] : []),
    ...(canManageOrderOperations ? [{
      key: 'edit-number',
      icon: <EditOutlined />,
      label: t('orders:orderDetail.editNumber'),
    }] : []),
    // WP12 B1: offered where the delete is refused (released WZ etc.). The
    // refusal itself is not repeated as a header alert (owner 2026-10-12).
    ...(canArchiveOrder ? [{
      key: 'archive',
      danger: true,
      label: t('orders:archive.action'),
    }] : []),
    ...(canRevertDelivery && order.status === 'DELIVERED' ? [{
      key: 'revert-delivery',
      danger: true,
      label: t('orders:deliveryRevert.button'),
    }] : []),
  ];
  const handleHeaderMenu = (key: string) => {
    if (key === 'pdf-pl' || key === 'pdf-en') {
      const language: OrderDocumentLanguage = key === 'pdf-pl' ? 'pl' : 'en';
      setOrderDocumentLanguage(language);
      void handleDownloadOrderDocument(language);
    } else if (key === 'customer-confirmation') {
      setCustomerEmailModalVisible(true);
    } else if (key === 'status-email') {
      setStatusEmailPurpose('status');
      setStatusEmailModalVisible(true);
    } else if (key === 'edit-number') {
      setNumberTaken(null);
      setNumberModalOpen(true);
    } else if (key === 'archive') {
      void handleArchiveOrder();
    } else if (key === 'revert-delivery') {
      setDeliveryRevertModalVisible(true);
    }
  };

  // The line editor lives in exactly one place: the editable Szkic step,
  // otherwise the „Edytuj pozycje” panel or the first details tab (never both open:
  // two editors would hold two edit states).
  const canEditOnAnyStep = canEditCustomerOrderOnAnyStep(currentUser);
  const canOpenLinesEditor = canEditOnAnyStep && canEditProducts && !draftEditable && order.productsLoaded;
  const productsSection = (
    <OrderProductsSection
      order={order}
      canEditProducts={canEditProducts}
      canViewSalePrices={canViewCustomerSalePrices}
      canViewMargin={canViewMargin}
      canViewPlannedMargin={canViewPlannedMargin}
      canEditSalePrices={canEditSalesDocuments}
      salePricesIncomplete={salePricesIncomplete}
      onSaved={refreshOrderFinancials}
    />
  );

  // One cost card: the „Koszty” panel in the header and the Finanse details tab.
  const orderCostsCard = canViewCustomerOrderCostSection && (
    <Card
      title={renderSectionTitle(t('orders:orderDetail.orderCosts'))}
      style={SECTION_CARD_STYLE}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={SECTION_CARD_BODY_STYLE}
      extra={
        <Space>
          {canEditCustomerOrderFallbackCosts && (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddCostModal}>
              {t('orders:orderDetail.addCost')}
            </Button>
          )}
        </Space>
      }
    >
      <Table
        dataSource={customerOrderCostRows}
        columns={operationalCostColumns}
        rowKey="id"
        size="middle"
        pagination={false}
        locale={{ emptyText: t('orders:orderDetail.noOrderCosts') }}
        summary={(data) => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><strong>{t('orders:orderDetail.total')}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <strong>{formatOrderMoney(data.reduce((sum: number, cost: any) => sum + getCostAmountInPln(cost), 0), 'PLN')}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} colSpan={Math.max(operationalCostColumns.length - 2, 1)} />
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
  // „Koszty” from the Oferta step on (PR #298, DESIGN.md): an order still in Szkic has none.
  const canOpenCostsPanel = canEditOnAnyStep && canViewCustomerOrderCostSection && canEditCustomerOrderFallbackCosts
    && order.status !== 'NEW_INQUIRY';

  // Steps 1–3 follow the first mockup (`process/guide`): the step card replaces the sticky bar.
  const guideActive = isOrderGuideStep(viewedStep);
  const manualStatusButton = (type: 'default' | 'link' = 'default') => canChangeOrderStatus && (
    <Tooltip title={orderBlockedTooltip('changeStatus')}>
      <Button
        type={type}
        disabled={Boolean(orderBlockedFor('changeStatus'))}
        onClick={() => {
          setStatusModalVisible(true);
          // The card loads the order once; another person may have moved or
          // reverted it since, so the status list must start from the saved one.
          void refreshOrder().catch(() => undefined);
        }}
        data-testid="zk-manual-status"
      >
        {t('orders:process.bar.manualStatus')}
      </Button>
    </Tooltip>
  );
  // „Zamknij zamówienie” (Emil, 2026-10-19): ADMIN closes a ZK whose approved
  // sales invoices cover only part of it (not a cancelled or archived one);
  // the backend owns the rules.
  // A ZK settled by a settlement invoice (decision 2026-11-02; an advance
  // alone does not settle it) has no quantity coverage; it closes by hand too
  // until all its goods are issued.
  const settledBySettlement = order.settledBySettlementInvoice === true;
  const manualCloseButton = canCloseOrderManually && !order.archivedAt
    && order.status !== 'INVOICED' && order.status !== 'CANCELLED'
    && (order.invoiceCoverage?.state === 'PARTIAL' || settledBySettlement) && order.invoiceCoverage && (
    <OrderManualCloseButton
      orderId={order.id}
      orderNumber={orderDisplayNumber}
      coverage={order.invoiceCoverage}
      advances={settledBySettlement}
      onClosed={async () => {
        setViewedStepKey(null);
        await refreshOrder();
        window.setTimeout(() => processData.refresh(), 2500);
      }}
    />
  );
  const saveDraftButton = () => draftEditable && (
    <Button icon={<SaveOutlined />} loading={draftEditor.saving} onClick={() => { void saveDraft(); }} data-testid="zk-draft-save">
      {t('orders:process.bar.saveDraft')}
    </Button>
  );
  // „Uzupełnij ceny” needs a saved draft the role may price.
  const canOpenPrices = draftEditable && canEditProducts && canViewCustomerSalePrices && canEditSalesDocuments;
  const handleSavePrices = async (prices: Record<string, number>) => {
    const saved = await saveDraft((current) => ({
      ...current,
      lines: current.lines.map((line) => (prices[line.key] !== undefined ? { ...line, unitPrice: prices[line.key] } : line)),
    }));
    if (saved) setPricesDialogOpen(false);
  };
  const guideProps = {
    ctx: stepContext,
    bar: stepBar,
    previewOnly: viewedReadOnly,
    busy: statusSubmitting || draftEditor.saving,
    onPrimary: (action: OrderStepPrimaryAction) => { void handleStepPrimary(action); },
    manualStatus: manualStatusButton('link'),
    onDeliveryModeChange: (mode: OrderDeliveryMode) => { void handleDeliveryModeChange(mode); },
    savingDelivery: savingDeliveryMode,
  };

  // ZK paid on a proforma: the proforma is a sales document of the ZK (invoice import, kind „Proforma”,
  // never approved); the payment day is a customer payment field. Unknown to a reader = undefined.
  const prepaymentFacts: OrderGuidePrepaymentFacts | null = order.paymentMode === 'PREPAYMENT' ? {
    percent: formatPrepaymentPercent(order.prepaymentPercent ?? DEFAULT_PREPAYMENT_PERCENT),
    proformaNumber: salesInvoices === null ? undefined : (() => {
      const proforma = salesInvoices.find((invoice) => invoice.documentKind === 'PROFORMA' && invoice.state !== 'WITHDRAWN');
      return proforma ? proforma.number || '' : null;
    })(),
    paidAt: canViewCustomerPayments
      ? (order.prepaymentPaidAt ? formatDateEuropean(order.prepaymentPaidAt) : null)
      : undefined,
    advanceNumber: salesInvoices === null ? undefined : (() => {
      const advance = salesInvoices.find((invoice) => invoice.documentKind === 'ADVANCE' && invoice.state === 'APPROVED');
      return advance ? advance.number || '' : null;
    })(),
  } : null;
  const canMarkPrepaymentPaid = draftRights.customerPayments && !order.archivedAt;
  const prepaymentActions = prepaymentFacts && viewedStep === 'CONFIRMED' ? (
    prepaymentFacts.advanceNumber === null && authService.canManageInvoiceImports() && !order.archivedAt ? (
      <Button onClick={() => navigate(`/app/sales-invoices/new?orderId=${order.id}&kind=ADVANCE`)} data-testid="zk-guide-add-advance">
        {t('orders:paymentMode.addAdvance')}
      </Button>
    ) : null
  ) : prepaymentFacts ? (
    <>
      {prepaymentFacts.proformaNumber === null && authService.canManageInvoiceImports() && !order.archivedAt && (
        <Button onClick={() => navigate(`/app/sales-invoices/new?orderId=${order.id}&kind=PROFORMA`)} data-testid="zk-guide-add-proforma">
          {t('orders:paymentMode.addProforma')}
        </Button>
      )}
      {canMarkPrepaymentPaid && (
        <Button onClick={() => setPrepaymentDialogOpen(true)} data-testid="zk-guide-prepayment-paid">
          {t(order.prepaymentPaidAt ? 'orders:paymentMode.changePaid' : 'orders:paymentMode.markPaid')}
        </Button>
      )}
    </>
  ) : null;

  const renderStepScreen = () => {
    switch (viewedStep) {
      case 'DRAFT':
        return (
          <OrderGuideScreen
            step="DRAFT"
            {...guideProps}
            prepayment={prepaymentFacts}
            draft={draftEditable ? draftEditor.draft : undefined}
            canOpenPrices={canOpenPrices}
            onOpenPrices={() => setPricesDialogOpen(true)}
            secondary={saveDraftButton()}
            marginStale={draftEditable && draftEditor.linesDirty}
          >
            <DraftStep
              ctx={{ ...stepContext, readOnly: !draftEditable }}
              editor={draftEditor}
              canEditLines={canEditProducts}
              canViewSalePrices={canViewCustomerSalePrices}
              canEditSalePrices={canEditSalesDocuments}
              rights={draftRights}
              customerName={customerName}
              customerAddress={order.customer?.address}
            />
          </OrderGuideScreen>
        );
      case 'OFFER':
        return (
          <OrderGuideScreen step="OFFER" {...guideProps} prepayment={prepaymentFacts} prepaymentActions={prepaymentActions}>
            <OfferStep
              ctx={stepContext}
              acceptance={offerAcceptance}
              onAcceptanceChange={setOfferAcceptance}
              documentLanguage={orderDocumentLanguage}
              onDocumentLanguageChange={setOrderDocumentLanguage}
              onDownloadPdf={() => { void handleDownloadOrderDocument(); }}
              pdfDownloading={orderDocumentDownloading}
              loadPdfPreview={canDownloadOrderPdf && id ? (language) => downloadOrderPdf(id, language) : undefined}
              onSendOffer={canSendCustomerEmails ? () => {
                clearStaleToasts();
                setStatusEmailPurpose('offer');
                setStatusEmailModalVisible(true);
              } : undefined}
              sendOfferDisabledReason={customerStatusEmail?.canSend ? null : (customerStatusEmailDisabledReason || null)}
            />
          </OrderGuideScreen>
        );
      case 'CONFIRMED':
        return (
          <OrderGuideScreen
            step="CONFIRMED"
            {...guideProps}
            prepayment={prepaymentFacts}
            prepaymentActions={prepaymentActions}
            onSendCustomerConfirmation={() => setCustomerEmailModalVisible(true)}
          />
        );
      case 'FULFILLMENT':
        return (
          <FulfillmentStep
            ctx={stepContext}
            onOpenTransportLink={() => setTransportLinkOpen(true)}
            onDeliveryScheduleChanged={() => {
              processData.refresh();
              void refreshOrderTransports();
            }}
            onTransportStatusChange={canManageTransportStatus ? handleTransportStatusChange : undefined}
            onUnlinkTransport={canDeleteTransportLink ? async (transportId) => {
              await handleUnlinkTransport(transportId);
              processData.refresh();
            } : undefined}
          />
        );
      case 'READY':
        return (
          <ReadyStep
            ctx={stepContext}
            onDeliveryModeChange={(mode) => { void handleDeliveryModeChange(mode); }}
            onFactoryDirect={() => { void handleFactoryDirect(); }}
            onOpenTransportLink={() => setTransportLinkOpen(true)}
            savingDeliveryMode={savingDeliveryMode}
          />
        );
      case 'DELIVERY':
        return <DeliveryStep ctx={stepContext} onIssueWz={() => setWzIssueOpen(true)} />;
      case 'DELIVERED':
        return <DeliveredStep ctx={stepContext} />;
      case 'INVOICE':
      default:
        return <InvoiceStep ctx={stepContext} onUploadInvoice={() => navigate(newSalesInvoiceUrl())}
          salesInvoices={salesInvoices} salesInvoicesFailed={salesInvoicesFailed} advanceBalance={advanceBalance}
          onRetrySalesInvoices={() => { void loadSalesInvoices(); }} onInvoicesChanged={handleSalesInvoicesChanged} />;
    }
  };

  return (
    <div className="zk-page">
      <div style={PAGE_SHELL_STYLE} className="order-detail-page zk-process">
      <PresenceBanner scopeType="ORDER" others={presenceOthers} style={{ marginBottom: 16 }} />
      <Modal
        open={numberModalOpen}
        title={t('orders:orderDetail.editNumber')}
        onCancel={() => setNumberModalOpen(false)}
        onOk={saveOrderNumber}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingField === 'manualNumbering'}
        destroyOnClose
      >
        <Form form={numberForm} layout="vertical" initialValues={getNumberModalInitialValues()}
          onValuesChange={() => setNumberTaken(null)}>
          <Form.Item
            name="useManualNumbering"
            valuePropName="checked"
            label={t('orders:orderDetail.useManualNumbering')}
          >
            <Switch />
          </Form.Item>
          {numberFormUseManual && (
            <Form.Item
              name="manualOrderNumber"
              label={t('orders:orderNumber')}
              validateStatus={numberTaken ? 'error' : undefined}
              help={numberTaken ? <OrderNumberTakenHelp taken={numberTaken} /> : undefined}
              rules={[
                { required: true, whitespace: true, message: t('orders:modal.validation.manualNumberRequired') },
                customerOrderNumberRule(t),
              ]}
            >
              <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <OrderProcessHeader
        orderNumber={<>
          {getDisplayOrderNumber(order)}
          {order.useManualNumbering && <Tag color="blue" className="zk-head__manual">{t('orders:orderDetail.manual')}</Tag>}
        </>}
        viewedStep={viewedStep}
        hideStepCopy={guideActive}
        pillKey={getOrderProcessPillKey(order.status, { pickup, invoiceCoverage: order.invoiceCoverage?.state })}
        copy={processCopy}
        pillTone={order.status === 'INVOICED' ? 'done' : (order.status === 'ON_HOLD' || order.status === 'CANCELLED') ? 'warning' : 'default'}
        customerName={customerName}
        createdAt={formatDateEuropean(order.createdAt)}
        orderDate={<OrderBusinessDate order={order} canEdit={canEditOrderDate}
          disabled={draftEditor.dirty} onSaved={refreshOrder} />}
        currency={order.currency || 'PLN'}
        accountManagerName={order.accountManagerName || createdByName || '-'}
        margin={marginFigure}
        marginVisibility={marginVisibility}
        marginPending={viewedStep === 'DRAFT' && draftEditable && draftEditor.linesDirty ? 'stale' : undefined}
        back={(
          <Button icon={<ArrowLeftOutlined />} onClick={() => confirmLeaveDraft(() => goBack('/app/orders'))}>
            {t('orders:process.back')}
          </Button>
        )}
        actions={<>
          {authService.canManageSupplierOrders() && canCreateSupplierDrafts(order) && <SupplierDraftGrouping orderId={order.id} orderNumber={getDisplayOrderNumber(order)} onCreated={refreshSupplierDraft} />}
          {canOpenLinesEditor && (
            <Button icon={<EditOutlined />} onClick={() => { setDetailsOpen(false); setLinesEditorOpen(true); }} data-testid="zk-edit-lines">
              {t('orders:process.editLines')}
            </Button>
          )}
          {canOpenCostsPanel && (
            <Button icon={<DollarOutlined />} onClick={() => { setDetailsOpen(false); setCostsDrawerOpen(true); }} data-testid="zk-edit-costs">
              {t('orders:process.editCosts')}
            </Button>
          )}
          {ORDER_DETAILS_BUTTON_VISIBLE && (
            <Button onClick={() => setDetailsOpen(true)} data-testid="zk-details-open">{t('orders:process.detailsTitle')}</Button>
          )}
          {headerMenuItems.length > 0 && (
            <Dropdown menu={{ items: headerMenuItems, onClick: ({ key }) => handleHeaderMenu(key) }} trigger={['click']}>
              <Button icon={<MoreOutlined />} loading={orderDocumentDownloading} data-testid="zk-more-actions">{t('orders:process.more')}</Button>
            </Dropdown>
          )}
        </>}
        alerts={(order.archivedAt || isFinancialCompletenessPending || order.commerciallyFrozenAt
          || showCustomerConfirmationBlockedReason || (canViewOrderTransports && !transportLoading && orderTransports.transports.length > 0)) ? <>
          {order.archivedAt && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:archive.banner', { date: formatDateEuropean(order.archivedAt) })}
              description={currentUser?.role === 'ADMIN'
                ? t('orders:archive.bannerAdmin')
                : t('orders:archive.bannerOthers')}
              action={currentUser?.role === 'ADMIN' ? (
                <Button size="small" onClick={handleUnarchiveOrder}>{t('orders:archive.unarchive')}</Button>
              ) : undefined}
            />
          )}
          {(isFinancialCompletenessPending || order.commerciallyFrozenAt) && (
            <Space size={8} wrap>
              {isFinancialCompletenessPending && <Tag color="gold">{t('orders:orderDetail.financialCompletenessPending')}</Tag>}
              {order.commerciallyFrozenAt && (
                <Tooltip title={t('orders:orderDetail.invoicedHint')}>
                  {order.invoiceCoverage && order.invoiceCoverage.state !== 'NONE'
                    ? <span><InvoiceCoverageTag coverage={order.invoiceCoverage} /></span>
                    : <Tag>{t('orders:orderDetail.invoicedTag')}</Tag>}
                </Tooltip>
              )}
            </Space>
          )}
          {showCustomerConfirmationBlockedReason && (
            <Alert type="warning" showIcon message={customerConfirmationDisabledReason} />
          )}
          {canViewOrderTransports && !transportLoading && (
            <OrderTransportStatusStrip transports={orderTransports.transports} />
          )}
        </> : undefined}
      />

      <OrderDocumentCompletenessSection visible={authService.canViewOrderDocumentCompleteness()} value={order.documentCompleteness} status={order.status}
        transportActionLabel={order.fulfillmentSource === 'FACTORY_DIRECT' ? t('orders:process.ready.transportTitle') : undefined}
        onPlanTransport={canManageOrderOperations && canViewOrderTransports ? () => setTransportLinkOpen(true) : undefined} />

      {order.isQuote && (
        <Alert
          type="info"
          showIcon
          message={t('orders:orderDetail.quoteBanner')}
          action={
            canManageOrderOperations && (
              <Button size="small" type="primary" onClick={handleConvertQuote}>
                {t('orders:orderDetail.convertToOrder')}
              </Button>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {order.quoteConvertedAt && !order.isQuote && (
        <Alert
          type="success"
          showIcon
          message={t('orders:orderDetail.convertedFromQuote', { date: formatDateEuropean(order.quoteConvertedAt) })}
          style={{ marginBottom: 16 }}
        />
      )}

      <OrderProcessRail
        position={processPosition}
        viewed={viewedStep}
        closed={order.status === 'INVOICED'}
        copy={processCopy}
        onView={(key) => confirmLeaveDraft(() => {
          clearStaleToasts();
          setViewedStepKey(key === processPosition.current ? null : key);
          window.scrollTo({ top: 0 });
        })}
      />

      {renderStepScreen()}

      {/* The „Faktura” stage shows its own; the current earlier stage lists invoices that already exist
          (partial, after withdrawal). Previews of finished stages do not repeat them (owner 2026-10-20). */}
      {viewedStep !== 'INVOICE' && !viewedReadOnly && hasOrderInvoices({ coverage: order.invoiceCoverage, salesInvoices, salesInvoicesFailed }) && (
        <div className="zk-col" style={{ marginTop: 16 }}>
          <OrderInvoicesStepSection title={t('orders:process.invoice.invoicesTitle')} coverage={order.invoiceCoverage}
            salesInvoices={salesInvoices} salesInvoicesFailed={salesInvoicesFailed} advanceBalance={advanceBalance}
            onRetrySalesInvoices={() => { void loadSalesInvoices(); }} onInvoicesChanged={handleSalesInvoicesChanged} />
        </div>
      )}

      <Drawer
        title={t('orders:process.editLinesTitle')}
        open={linesEditorOpen && canOpenLinesEditor}
        onClose={() => setLinesEditorOpen(false)}
        width="min(1180px, 100vw)"
        destroyOnHidden
        data-testid="zk-lines-drawer"
      >
        <OrderProductsSection
          order={order}
          canEditProducts={canEditProducts}
          canViewSalePrices={canViewCustomerSalePrices}
          canViewMargin={canViewMargin}
          canViewPlannedMargin={canViewPlannedMargin}
          canEditSalePrices={canEditSalesDocuments}
          salePricesIncomplete={salePricesIncomplete}
          startEditing
          onCancelEdit={() => setLinesEditorOpen(false)}
          onSaved={async () => {
            setLinesEditorOpen(false);
            await refreshOrderFinancials();
            processData.refresh();
          }}
        />
      </Drawer>

      <Drawer
        title={t('orders:orderDetail.orderCosts')}
        open={costsDrawerOpen && canOpenCostsPanel}
        onClose={() => setCostsDrawerOpen(false)}
        width="min(1180px, 100vw)"
        destroyOnHidden
        data-testid="zk-costs-drawer"
      >
        {orderCostsCard}
      </Drawer>

      {/* Makieta nie ma zakładek szczegółów pod krokiem: dostępne z nagłówka („Szczegóły zamówienia”) w panelu bocznym. */}
      <Drawer
        title={t('orders:process.detailsTitle')}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        width="min(980px, 100vw)"
        destroyOnHidden
        data-testid="zk-details-drawer"
      >
      <section className="zk-section zk-details" data-testid="zk-details">
      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        size="large"
        style={{ maxWidth: '100%', minWidth: 0 }}
        tabBarStyle={{ marginBottom: 24, fontWeight: 600, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}
      >
        <Tabs.TabPane tab={t('orders:orderDetail.orderInfo')} key="1">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
              {authService.canViewWarehouseDocuments() && (
                <OrderWarehouseSection
                  orderId={order.id}
                  canViewMargin={canViewMargin}
                  refreshKey={order}
                  onRefreshOrder={refreshOrder}
                  resultLabel={t(ORDER_MARGIN_DETAILS_LABEL_KEYS[marginFigure.stage])}
                  canIssue={order.status !== 'INVOICED' && !processFacts.allIssued}
                  products={order.productsLoaded ? order.products : []}
                />
              )}
              {!draftEditable && !linesEditorOpen && productsSection}
              <OrderInvoiceCoverageSection coverage={order.invoiceCoverage} defaultOpen salesInvoices={salesInvoices} advanceBalance={advanceBalance}
                salesInvoicesFailed={salesInvoicesFailed} onRetrySalesInvoices={() => { void loadSalesInvoices(); }}
                onInvoicesChanged={handleSalesInvoicesChanged} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
              <OrderInfoAccordion
                order={order}
                customerName={customerName}
                createdByName={createdByName}
                accountManagers={accountManagers}
                suppliers={suppliers}
                customerPaymentStatus={customerPaymentStatus}
                canViewCustomerPayments={canViewCustomerPayments}
                isCustomerPaymentOverdue={isCustomerPaymentOverdue}
                useManualNumberingValue={Boolean(useManualNumberingValue)}
                editingField={editingField}
                editForm={editForm}
                renderEditableField={renderEditableField}
                canEditOrderDate={canEditOrderDate}
                canEditCommercialFields={canEditCustomerOrderCommercialFields(currentUser)}
                canEditAccountManager={canEditCustomerOrderAccountManager(currentUser)}
                canEditCustomerOrderDocuments={canEditCustomerOrderDocuments}
                canEditForecastMonth={canEditCustomerOrderForecastMonth(currentUser)}
                canEditDeliveryMode={canEditCustomerOrderDeliveryMode(currentUser)}
              />
            </motion.div>
          </Space>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <FileTextOutlined />
              {t('orders:orderDetail.activity')}
            </span>
          }
          key="activity"
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
              <Card
                title={renderSectionTitle(t('orders:orderDetail.statusHistory'))}
                style={SECTION_CARD_STYLE}
                headStyle={SECTION_CARD_HEAD_STYLE}
                bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
              >
                <Timeline style={{ marginBottom: 0 }}>
                  {orderActivityHistory.map((history: any) => (
                    <Timeline.Item key={history.id}>
                      <div>
                        <strong>{history.title || t(`orders:statusOptions.${history.status}`)}</strong>
                        <div style={{ fontSize: 12, color: 'var(--app-text-muted)' }}>
                          {formatDateTimeEuropean(history.changedAt)}
                          {' · '}
                          {history.changedBy?.name || t('orders:orderDetail.historyAuthorUnknown')}
                        </div>
                        {history.notes && <div style={{ fontSize: 12 }}>{history.notes}</div>}
                        {history.notificationLog && canViewCommunications && (
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => setOpenedNotificationLog(history.notificationLog)}
                          >
                            {t('orders:process.activity.showMessage')}
                          </Button>
                        )}
                        {history.supplierOrderId && (
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => navigate(`/app/supplier-orders/${history.supplierOrderId}`)}
                          >
                            {t('orders:orderDetail.supplierDraftHistory.open')}
                          </Button>
                        )}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </motion.div>

            {/* Notification Assignments - Admin only */}
            {canManageNotificationAssignments && (
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}>
                <OrderNotificationAssignments orderId={id!} />
              </motion.div>
            )}

            <CommunicationDetail
              record={openedNotificationLog}
              open={openedNotificationLog !== null}
              onClose={() => setOpenedNotificationLog(null)}
            />
          </Space>
        </Tabs.TabPane>

    {canViewOrderTransports && (
    <Tabs.TabPane
      tab={
        <span>
          <TruckOutlined />
          {t('transport:transport')}
          {orderTransports.transports.length > 0 && (
            <Tag style={{ marginLeft: 8 }}>{orderTransports.transports.length}</Tag>
          )}
        </span>
      }
      key="2"
    >
      <OrderTransportsSection
        orderId={id!}
        orderTransports={orderTransports}
        loading={transportLoading}
        canEdit={canManageOrderOperations}
        canDelete={canDeleteTransportLink}
        canManageStatus={canManageTransportStatus}
        canViewCosts={authService.canViewTransportCosts()}
        products={order.productsLoaded ? order.products : []}
        customerPickup={order.deliveryMode === 'CUSTOMER_PICKUP'}
        orderLabel={orderDisplayNumber}
        deliveryPlace={resolveOrderDeliveryAddress(order)}
        onSwitchToOurDelivery={canManageOrderOperations ? switchToOurDelivery : undefined}
        onTransportsChanged={() => {
          void refreshOrderTransports();
          void refreshOrderFinancials();
        }}
        onDelete={handleUnlinkTransport}
        onStatusChange={handleTransportStatusChange}
      />
    </Tabs.TabPane>
    )}

    {(canViewCustomerOrderCostSection || canViewCustomerSalePrices || canViewPurchaseDocuments || canViewMargin) && (
    <Tabs.TabPane
      tab={
        <span>
          <DollarOutlined />
          {t('orders:orderDetail.finances')}
        </span>
      }
      key="4"
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {canViewMargin && !hasLedgerSettlement && revenueDisplayAmount <= 0 && totalCostDisplayAmount > 0 && (
          <Alert
            type="info"
            showIcon
            message={t('orders:orderDetail.costWithoutRevenueTitle')}
            description={t('orders:orderDetail.costWithoutRevenueDescription')}
          />
        )}
        {canViewMargin && (
        <Card
          title={renderSectionTitle(t('orders:orderDetail.financialSummary'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
        >
          {settlementDetails && settlementDetails.entries.length > 0 && (
            <Card
              size="small"
              title={t('orders:orderDetail.settlementHistory')}
              style={{ marginBottom: 16 }}
            >
              <Table<OrderSettlementEntry>
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 760 }}
                dataSource={settlementDetails.entries}
                columns={[
                  {
                    title: t('orders:orderDetail.settlementDate'),
                    dataIndex: 'recognizedAt',
                    key: 'recognizedAt',
                    render: (value: string) => formatDateEuropean(value),
                  },
                  {
                    title: t('orders:orderDetail.settlementKind'),
                    dataIndex: 'kind',
                    key: 'kind',
                    render: (value: OrderSettlementEntry['kind']) => value === 'PARTIAL'
                      ? t('orders:orderDetail.settlementInvoiceEntry')
                      : t(`orders:orderDetail.settlementKinds.${value}`),
                  },
                  {
                    title: t('orders:orderDetail.settlementRevenue'),
                    dataIndex: 'revenue',
                    key: 'revenue',
                    align: 'right',
                    render: (value: number | null) => value === null ? pendingInvoiceCostLabel : formatOrderMoney(value, 'PLN'),
                  },
                  {
                    title: t('orders:orderDetail.settlementCost'),
                    dataIndex: 'totalCost',
                    key: 'totalCost',
                    align: 'right',
                    render: (value: number | null) => value === null ? pendingInvoiceCostLabel : formatOrderMoney(value, 'PLN'),
                  },
                  {
                    title: t('orders:orderDetail.settlementNote'),
                    dataIndex: 'note',
                    key: 'note',
                    render: (value: string | null) => value || '—',
                  },
                ]}
              />
            </Card>
          )}
          {hasLedgerSettlement && settlementDetails ? <>
            <Alert type={settlementDetails.costStatus === 'PENDING' ? 'warning' : 'info'} message={settlementDetails.costStatus === 'PENDING' ? pendingInvoiceCostLabel : t('invoices:workflow.page.invoiceFinancialBasis')} style={{ marginBottom: 16 }} />
            {settlementDetails.unsettledLines.length > 0 && (
              <Alert type="info" showIcon data-testid="order-unsettled-lines" style={{ marginBottom: 16 }}
                message={t('orders:orderDetail.settlementUnsettledLines', { lines: formatRemainderLines(settlementDetails.unsettledLines) })} />
            )}
            {settlementDetails.issuedOverInvoice.length > 0 && (
              <Alert type="warning" showIcon data-testid="order-issued-over-invoice" style={{ marginBottom: 16 }}
                message={t('orders:orderDetail.settlementIssuedOverInvoice', { lines: settlementDetails.issuedOverInvoice.map((line) => `${line.name} (${formatLocalizedQuantity(line.quantity)} ${formatUnit(line.unit)})`).join(', ') })} />
            )}
            <div className="order-financial-summary">
              {([
                ['revenue', 'revenue'], ['totalCost', 'totalCost'], ['margin', 'orderResult'], ['marginPercent', 'margin'],
              ] as const).map(([field, label]) => <div className="order-financial-summary__metric" key={field}>
                <span className="order-financial-summary__label">{t(`orders:orderDetail.${label}`)}</span>
                <strong className="order-financial-summary__value">{settlementDetails.actual[field] == null ? pendingInvoiceCostLabel : field === 'marginPercent' ? `${formatLocalizedQuantity(settlementDetails.actual[field], 'pl', 2)}%` : formatOrderMoney(settlementDetails.actual[field]!, 'PLN')}</strong>
              </div>)}
            </div>
          </> : <>
          {order.warehouseCostPending === true && (
            <Alert type="warning" showIcon data-testid="order-partial-cost" style={{ marginBottom: 16 }}
              message={t('orders:orderDetail.partialCostNotice')} />
          )}
          <div className="order-financial-summary">
            <div className="order-financial-summary__metric">
              <span className="order-financial-summary__label">{t('orders:orderDetail.revenue')}</span>
              <div className="order-financial-summary__value">
                <CurrencyAmount
                  amount={order.exchangeRate ? revenueDisplayAmount / order.exchangeRate : revenueDisplayAmount}
                  currency={order.currency}
                  plnAmount={revenueDisplayAmount}
                  rateDate={order.exchangeRateEffectiveDate || order.orderDate}
                  rateSource={order.exchangeRateSource}
                  rateTableNumber={order.exchangeRateTableNumber}
                  strong
                  primary="pln"
                />
              </div>
            </div>
            <div className="order-financial-summary__metric">
              <span className="order-financial-summary__label">{t('orders:orderDetail.totalCost')}</span>
              <strong className="order-financial-summary__value">{formatOrderMoney(totalCostDisplayAmount, 'PLN')}</strong>
            </div>
            <div className={`order-financial-summary__metric order-financial-summary__metric--result order-financial-summary__metric--${marginTone}`}>
              <span className="order-financial-summary__label">{t('orders:orderDetail.orderResult')}</span>
              <strong className="order-financial-summary__value">{formatOrderMoney(marginDisplayAmount, 'PLN')}</strong>
              <span className="order-financial-summary__meta">{t(`orders:orderDetail.${marginStatusKey}`)}</span>
            </div>
            <div className="order-financial-summary__metric">
              <span className="order-financial-summary__label">{t('orders:orderDetail.margin')}</span>
              <strong className="order-financial-summary__value">{marginPercentLabel}</strong>
            </div>
          </div>
          </>}
        </Card>
      )}
      {!canViewMargin && canViewPlannedMargin && <OrderPlannedMarginSummary order={order} />}
      {orderCostsCard}
      {canViewPurchaseDocuments && purchaseInvoiceCosts.length > 0 && (
      <Card
        title={renderSectionTitle(t('orders:orderDetail.purchaseInvoices'))}
        style={SECTION_CARD_STYLE}
        headStyle={SECTION_CARD_HEAD_STYLE}
        bodyStyle={SECTION_CARD_BODY_STYLE}
      >
        <Table
          dataSource={purchaseInvoiceCosts}
          columns={purchaseInvoiceCostColumns}
          rowKey="id"
          size="middle"
          pagination={false}
          locale={{ emptyText: t('orders:orderDetail.noPurchaseInvoices') }}
        />
      </Card>
      )}
      {canViewMargin && marginHistory.length > 0 && (
        <Card
          title={renderSectionTitle(t('orders:orderDetail.marginHistory'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
          size="small"
        >
          <Timeline>
            {marginHistory.map((h) => {
              const historyMarginPercent = Number.isFinite(h.marginPercent) ? h.marginPercent : 0;
              const historyPercentLabel = hasRevenueForMarginPercent(h.revenue)
                ? formatLocalizedPercent(historyMarginPercent, i18n.language)
                : t('orders:orderDetail.marginPercentUnavailable');

              return (
                <Timeline.Item key={h.id} color={historyMarginPercent < 15 ? 'red' : 'green'}>
                  <div>
                    <strong>{historyPercentLabel}</strong> ({formatOrderMoney(h.margin, 'PLN')})
                    <div style={{ fontSize: 12, color: 'var(--app-text-secondary)' }}>
                      {getMarginHistoryReasonLabel(h.reason, t)} · {formatDateTimeEuropean(h.changedAt)}
                    </div>
                    <div style={{ fontSize: 12 }}>{h.changedBy?.name || t('orders:orderDetail.historyAuthorUnknown')}</div>
                    {h.details?.reason && (
                      <div style={{ fontSize: 12 }}>{t('orders:orderDetail.marginHistoryReason')}: {h.details.reason}</div>
                    )}
                    {h.details?.lines && h.details.lines.length > 0 && (
                      <ul style={{ fontSize: 12, margin: '4px 0 0', paddingLeft: 16 }}>
                        {h.details.lines.map((line, lineIndex) => (
                          <li key={`${h.id}-${line.orderProductId || lineIndex}`}>
                            {t(`orders:orderDetail.marginHistoryLineChange.${line.change}`)}: {line.name || t('orders:orderDetail.marginHistoryLineUnnamed')}
                            {' — '}
                            {formatMarginHistoryLineImage(line.before)}
                            {' → '}
                            {formatMarginHistoryLineImage(line.after)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Card>
      )}
      </Space>
    </Tabs.TabPane>
    )}

    {canViewOrderTasks && (
    <Tabs.TabPane
      tab={
        <span>
          <CheckSquareOutlined />
          {t('tasks:orderDetailTab')}
          {orderTasks.length > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{orderTasks.length}</Tag>}
        </span>
      }
      key="5"
    >
      <Card
        title={t('tasks:orderDetailTab')}
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/app/tasks`)}
          >
            {t('tasks:newTask')}
          </Button>
        }
      >
        {orderTasksLoading ? (
          <Spin />
        ) : orderTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--app-text-muted)' }}>
            {t('tasks:noTasks')}
          </div>
        ) : (
          <Table
            dataSource={orderTasks}
            rowKey="id"
            size="small"
            pagination={false}
            onRow={(record) => ({
              style: { cursor: 'pointer' },
              onClick: () => navigate('/app/tasks')
            })}
            columns={[
              {
                title: t('tasks:taskTitle'),
                dataIndex: 'title',
                key: 'title',
                render: (text: string) => <strong>{text}</strong>
              },
              {
                title: t('tasks:status'),
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (status: string) => {
                  const colors: Record<string, string> = { NEW: 'default', IN_PROGRESS: 'processing', REVIEW: 'warning', DONE: 'success', CANCELLED: 'error' };
                  return <Tag color={colors[status]}>{t(`tasks:statuses.${status}`)}</Tag>;
                }
              },
              {
                title: t('tasks:priority'),
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: Record<string, string> = { LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', URGENT: 'red' };
                  return <Tag color={colors[priority]}>{t(`tasks:priorities.${priority}`)}</Tag>;
                }
              },
              {
                title: t('tasks:assignee'),
                dataIndex: 'assigneeName',
                key: 'assigneeName',
                width: 150,
                render: (name: string) => name ? <><UserOutlined /> {name}</> : <span style={{ color: 'var(--app-text-muted)', fontStyle: 'italic' }}>{t('tasks:unassigned')}</span>
              },
              {
                title: t('tasks:dueDate'),
                dataIndex: 'dueDate',
                key: 'dueDate',
                width: 120,
                render: (date: string, record: InternalTask) => {
                  if (!date) return '-';
                  const [y, m, d] = date.split('-').map(Number);
                  const dueLocal = new Date(y, m - 1, d);
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const isOverdue = dueLocal < today && record.status !== 'DONE' && record.status !== 'CANCELLED';
                  return <Tag color={isOverdue ? 'red' : undefined}><CalendarOutlined /> {dueLocal.toLocaleDateString()}</Tag>;
                }
              }
            ]}
          />
        )}
      </Card>
    </Tabs.TabPane>
    )}
  </Tabs>
      </section>
      </Drawer>
      </div>

      {(!guideActive || viewedReadOnly) && (
      <OrderStepActionBar
        bar={stepBar}
        previewOf={viewedReadOnly ? viewedStep : null}
        onBackToCurrent={() => { clearStaleToasts(); setViewedStepKey(null); }}
        onPrimary={(action) => { void handleStepPrimary(action); }}
        busy={statusSubmitting || draftEditor.saving}
        secondary={<>{manualCloseButton}{saveDraftButton()}</>}
        owner={ORDER_PROCESS_STEPS[getOrderProcessStepIndex(viewedStep)].owner}
        manualStatus={manualStatusButton()}
      />
      )}

      <OrderConfirmDialog
        open={confirmDialogOpen}
        orderId={order.id}
        orderNumber={orderDisplayNumber}
        acceptance={offerAcceptance}
        products={order.productsLoaded ? order.products : []}
        initialFulfillment={{
          source: order.fulfillmentSource === 'FACTORY_DIRECT' ? 'FACTORY_DIRECT' : 'WAREHOUSE',
          note: order.fulfillmentNote ?? '',
        }}
        submitting={statusSubmitting}
        onCancel={() => setConfirmDialogOpen(false)}
        onSubmit={(values) => { void submitStatusChange(values); }}
      />
      {canMarkPrepaymentPaid && order.paymentMode === 'PREPAYMENT' && (
        <PrepaymentPaidDialog
          open={prepaymentDialogOpen}
          paidAt={order.prepaymentPaidAt ?? null}
          saving={savingPrepayment}
          onSave={(paidAt) => { void handlePrepaymentPaidSave(paidAt); }}
          onCancel={() => setPrepaymentDialogOpen(false)}
        />
      )}
      {canMarkPrepaymentPaid && (
        <PrepaymentPaidDialog
          gate
          open={prepaymentGateOpen}
          paidAt={order.prepaymentPaidAt ?? null}
          saving={savingPrepayment}
          onSave={(paidAt) => { void handlePrepaymentPaidSave(paidAt, { thenConfirm: true }); }}
          onCancel={() => setPrepaymentGateOpen(false)}
        />
      )}
      {canOpenPrices && (
        <OrderPricesDialog
          open={pricesDialogOpen}
          orderId={order.id}
          orderNumber={orderDisplayNumber}
          lines={draftEditor.draft.lines}
          savedLines={draftEditor.savedLines}
          knownLines={processFifo.lines}
          marginVisibility={marginVisibility}
          orderCurrency={order.currency || 'PLN'}
          saving={draftEditor.saving}
          onCancel={() => setPricesDialogOpen(false)}
          onSave={(prices) => { void handleSavePrices(prices); }}
        />
      )}
      <OrderDeliverDialog
        open={deliverDialogOpen}
        allIssued={processFacts.allIssued}
        alreadyPartial={order.status === 'PARTIALLY_DELIVERED'}
        submitting={statusSubmitting}
        onCancel={() => setDeliverDialogOpen(false)}
        onSubmit={(values) => { void submitStatusChange(values); }}
      />
      {canManageWz && (
        <WzIssueModal
          orderId={order.id}
          open={wzIssueOpen}
          onClose={() => setWzIssueOpen(false)}
          onIssued={(result) => { void handleWzIssued(result); }}
        />
      )}
      {canViewOrderTransports && (
        <OrderTransportLinkModal
          open={transportLinkOpen}
          orderId={order.id}
          orderLabel={orderDisplayNumber}
          deliveryPlace={resolveOrderDeliveryAddress(order)}
          customerPickup={order.deliveryMode === 'CUSTOMER_PICKUP'}
          onSwitchToOurDelivery={canManageOrderOperations ? switchToOurDelivery : undefined}
          canViewCosts={authService.canViewTransportCosts()}
          onAttached={() => {
            void refreshOrderTransports();
            void refreshOrderFinancials();
            processData.refresh();
          }}
          onClose={() => setTransportLinkOpen(false)}
        />
      )}

      <Modal
        title={editingCost ? t('orders:orderDetail.editCost') : t('orders:orderDetail.addCost')}
        open={costModalVisible}
        onCancel={closeCostModal}
        maskClosable={false}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddCost}>
          <Form.Item
            name="currency"
            label={t('orders:currency')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="USD">{t('orders:currencies.USD')}</Select.Option>
              <Select.Option value="EUR">{t('orders:currencies.EUR')}</Select.Option>
              <Select.Option value="PLN">{t('orders:currencies.PLN')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label={t('orders:orderDetail.amount')}
            rules={[{ required: true }, { type: 'number', max: MAX_COST_AMOUNT }]}
          >
            <InputNumber min={0} max={MAX_COST_AMOUNT} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('orders:orderDetail.costDescription')}
            rules={[{ required: true }]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="document"
            label={t('orders:orderDetail.document')}
            valuePropName="fileList"
            getValueFromEvent={normalizeUploadFileList}
          >
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>{t('orders:orderDetail.uploadDocument')}</Button>
            </Upload>
          </Form.Item>
          {editingCost?.documentUrl && (
            <div style={{ marginBottom: 16 }}>
              {t('orders:orderDetail.document')}: <a href={getAuthFileUrl(editingCost.documentUrl)} target="_blank" rel="noreferrer">{editingCost.documentName || t('orders:orderDetail.document')}</a>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submittingCost}>
                {editingCost ? t('common:save') : t('orders:orderDetail.addCost')}
              </Button>
              <Button onClick={closeCostModal} disabled={submittingCost}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('orders:process.bar.manualStatus')}
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          setConfirmationNotice(null);
        }}
        maskClosable={false}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleUpdateStatus} onFinishFailed={() => feedback.warning(t('orders:messages.fillRequired'))} scrollToFirstError>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('orders:statusSelection.informationalTitle')}
            description={t('orders:statusSelection.informationalDescription')}
          />
          {orderDeliveryWarnings.length > 0 && (
            // WZ context for "Dostarczone" shown before the save, not only in
            // the post-save toast (orderDeliveryWZPolicy: warning, never a gate).
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('orders:blocked.actions.deliver')}
              description={orderDeliveryWarnings
                .map((row) => getBlockedActionMessage(row, t, 'orders', ORDER_ERROR_CODE_KEYS))
                .join(' ')}
            />
          )}
          <Form.Item
            name="status"
            label={t('orders:orderDetail.newStatus')}
            rules={[{ required: true }]}
          >
            <OrderStatusTransitionSelect
              statuses={CUSTOMER_ORDER_ACTIVE_STATUS_VALUES}
              currentStatus={order.status}
              allowedTransitions={getOrderAllowedTransitions(order)}
              getStatusLabel={(status) => t(`orders:statusOptions.${status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
              guardedStatuses={['ON_HOLD']}
              guardedStatusReason={t('orders:statusSelection.requiresExplicitAction')}
              guardedStatusActionLabel={() => t('orders:statusSelection.holdOrderAction')}
              guardedStatusHint={t('orders:statusSelection.holdOrderHint')}
              onGuardedStatusSelect={(status) => statusForm.setFieldValue('status', status)}
            />
          </Form.Item>
          {statusModalVisible && order.isQuote !== true
            && startsCustomerOrderConfirmationCycle(order.status, selectedStatus) && (
            <OrderConfirmationNoticeSection
              orderId={order.id}
              value={confirmationNotice}
              onChange={setConfirmationNotice}
            />
          )}
          <Form.Item name="notes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={statusSubmitting}>{t('orders:process.manualStatusSave')}</Button>
          </Form.Item>
        </Form>
      </Modal>

      {canRevertDelivery && (
        <OrderDeliveryRevertModal
          open={deliveryRevertModalVisible}
          orderId={order.id}
          onClose={() => setDeliveryRevertModalVisible(false)}
          onReverted={async (targetStatus) => {
            feedback.success(t('orders:deliveryRevert.success', {
              orderNumber: orderDisplayNumber,
              status: t(`orders:statusOptions.${targetStatus}`),
            }));
            await Promise.all([
              refreshOrder(),
              fetchMarginHistory(),
              canViewOrderTransports ? refreshOrderTransports() : Promise.resolve(),
            ]);
          }}
          onError={(error) => {
            feedback.error(getApiErrorMessage(error, t, 'orders:deliveryRevert.errors.failed', ORDER_DELIVERY_REVERT_ERROR_KEYS, { orderNumber: orderDisplayNumber }));
          }}
        />
      )}

      <CustomerEmailEditorModal
        title={t('orders:orderDetail.sendCustomerConfirmation')}
        open={customerEmailModalVisible}
        recipientName={order?.customer?.name}
        recipientEmail={order?.customer?.email}
        subject={customerEmailSubject}
        message={customerEmailMessage}
        previewHtml={customerEmailPreview?.html || ''}
        editing={customerEmailEditing}
        previewAvailable={Boolean(customerEmailPreview)}
        previewLoading={customerEmailPreviewLoading}
        sending={customerEmailSending}
        okText={t('orders:orderDetail.sendCustomerConfirmationOk')}
        previewErrorText={t('orders:orderDetail.customerConfirmationPreviewError')}
        onCancel={() => {
          setCustomerEmailModalVisible(false);
          setCustomerEmailEditing(false);
        }}
        onSubmit={handleSendCustomerConfirmation}
        onEdit={() => setCustomerEmailEditing(true)}
        onDiscardEdit={() => {
          setCustomerEmailEditing(false);
          setCustomerEmailPreview(customerEmailDefaultPreview);
          setCustomerEmailSubject(customerEmailDefaultPreview?.subject || '');
          setCustomerEmailMessage(customerEmailDefaultPreview?.message || '');
        }}
        onSubjectChange={setCustomerEmailSubject}
        onMessageChange={setCustomerEmailMessage}
      />

      <CustomerEmailEditorModal
        title={t(statusEmailPurpose === 'offer' ? 'orders:process.offer.sendTitle' : 'orders:orderDetail.sendCustomerStatusEmail')}
        open={statusEmailModalVisible}
        recipientName={order?.customer?.name}
        recipientEmail={order?.customer?.email}
        subject={statusEmailSubject}
        message={statusEmailMessage}
        previewHtml={statusEmailPreview?.html || ''}
        editing={statusEmailEditing}
        previewAvailable={Boolean(statusEmailPreview)}
        previewLoading={statusEmailPreviewLoading}
        sending={statusEmailSending}
        okText={t(statusEmailPurpose === 'offer' ? 'orders:process.offer.sendOk' : 'orders:orderDetail.sendCustomerStatusEmailOk')}
        previewErrorText={t('orders:orderDetail.customerStatusEmailPreviewError')}
        onCancel={() => {
          setStatusEmailModalVisible(false);
          setStatusEmailEditing(false);
          setStatusEmailPurpose('status');
        }}
        onSubmit={handleSendCustomerStatusEmail}
        onEdit={() => setStatusEmailEditing(true)}
        onDiscardEdit={() => {
          if (statusEmailPurpose === 'offer' && order) {
            // The offer is always the edited text: „discard” restores the offer wording.
            const offerText = buildOfferEmailText({ orderNumber: getDisplayOrderNumber(order) }, t);
            setStatusEmailSubject(offerText.subject);
            setStatusEmailMessage(offerText.message);
            return;
          }
          setStatusEmailEditing(false);
          setStatusEmailPreview(statusEmailDefaultPreview);
          setStatusEmailSubject(statusEmailDefaultPreview?.subject || '');
          setStatusEmailMessage(statusEmailDefaultPreview?.message || '');
        }}
        onSubjectChange={setStatusEmailSubject}
        onMessageChange={setStatusEmailMessage}
      />
    </div>
  );
};

export default OrderDetail;
